// src/components/admin/WhatsAppCampaignsEditor.jsx
// WhatsApp Campaign Editor with Message Categories (Transactional & Promotional)

import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  limit
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import whatsappService from '../../api/whatsappService';
import { getCustomerTargetingPreview, getSalonServices, getSalonStaff } from '../../api/salonApi';
import { applyIndustryContent, getSalonIndustry } from '../../utils/industryContent';
import { findUnresolvedPlaceholders, personalizeMessage, getSalonPhone, getSalonName, getCustomerAppointment, getCustomerTransaction } from '../../utils/campaignPersonalization';
import '../../styles/customer-preview-modal.css';
import CampaignFrequencyControl from './CampaignFrequencyControl';
import FinalCustomersTargeted from './FinalCustomersTargeted';
import { applyFrequencyExclusionToList, recordCampaignDeliveries } from '../../api/campaignExclusionService';
import { useSalonLocations } from '../../hooks/useSalonLocations';

// Customer Preview Modal Component
function CustomerPreviewModal({ isOpen, onClose, previewData, campaignName }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('asc');
  const customersPerPage = 100;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  if (!isOpen || !previewData) return null;

  const { eligibleCustomers = [], eligibleCount = 0, totalCount = 0 } = previewData;

  const filteredCustomers = eligibleCustomers.filter(customer => {
    if (!filterText) return true;
    const searchLower = filterText.toLowerCase();
    return (
      customer.displayName?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.phoneNumber?.toLowerCase().includes(searchLower)
    );
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';

    if (sortDirection === 'asc') {
      return aVal.localeCompare(bVal);
    } else {
      return bVal.localeCompare(aVal);
    }
  });

  const totalPages = Math.ceil(sortedCustomers.length / customersPerPage);
  const startIndex = (currentPage - 1) * customersPerPage;
  const endIndex = startIndex + customersPerPage;
  const currentCustomers = sortedCustomers.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Phone Number', 'Email'];
    const csvRows = [headers.join(',')];

    sortedCustomers.forEach(customer => {
      const row = [
        `"${customer.displayName || ''}"`,
        `"${customer.phoneNumber || ''}"`,
        `"${customer.email || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const date = new Date().toISOString().split('T')[0];
    const filename = `whatsapp-campaign-recipients-${campaignName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${date}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const coverage = totalCount > 0 ? ((eligibleCount / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="modal-overlay">
      <div className="customer-preview-modal">
        <div className="modal-header">
          <h3>WhatsApp Recipients Preview</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-summary">
          <div className="summary-item">
            <span className="summary-label">Eligible Recipients</span>
            <span className="summary-value">{eligibleCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Customers</span>
            <span className="summary-value">{totalCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Coverage</span>
            <span className="summary-value coverage">{coverage}%</span>
          </div>
        </div>

        <div className="modal-body">
          {eligibleCount > 0 ? (
            <>
              <button
                className="export-csv-btn"
                onClick={handleExportCSV}
                disabled={sortedCustomers.length === 0}
              >
                📥 Export to CSV
              </button>

              <div className="table-filter">
                <input
                  type="text"
                  placeholder="Search by name, phone or email..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button
                    className="filter-clear-btn"
                    onClick={() => setFilterText('')}
                  >
                    Clear
                  </button>
                )}
              </div>

              {currentCustomers.length > 0 ? (
                <>
                  <div className="customer-table-container">
                    <table className="customer-table">
                      <thead>
                        <tr>
                          <th
                            onClick={() => handleSort('displayName')}
                            className={sortField === 'displayName' ? `sorted-${sortDirection}` : ''}
                          >
                            Customer Name
                            <span className="sort-indicator"></span>
                          </th>
                          <th
                            onClick={() => handleSort('phoneNumber')}
                            className={sortField === 'phoneNumber' ? `sorted-${sortDirection}` : ''}
                          >
                            Phone Number
                            <span className="sort-indicator"></span>
                          </th>
                          <th
                            onClick={() => handleSort('email')}
                            className={sortField === 'email' ? `sorted-${sortDirection}` : ''}
                          >
                            Email Address
                            <span className="sort-indicator"></span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentCustomers.map((customer, index) => (
                          <tr key={customer.id || index}>
                            <td>{customer.displayName || 'N/A'}</td>
                            <td>{customer.phoneNumber || 'N/A'}</td>
                            <td>{customer.email || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>

                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">🔍</div>
                  <p className="empty-state-text">No customers match your search</p>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <p className="empty-state-text">No eligible recipients found for the selected criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TEMPLATE_META = {
  booking_confirmed:     { label: 'Booking Confirmed 🎉',       category: 'transactional', trigger: 'when a booking is approved, created or rescheduled', vars: ['Customer name','Salon & location','Date','Time','Service','Stylist'] },
  appointment_reminder:  { label: 'Appointment Reminder 🗓️',    category: 'transactional', trigger: 'the day before an approved appointment (from 9am)', vars: ['Customer name','Appointment time','Stylist','Service','Salon & location'] },
  booking_cancelled:     { label: 'Booking Cancelled',          category: 'transactional', trigger: 'when the salon cancels a booking', vars: ['Customer name','Salon & location','Date','Time'] },
  booking_rejected:      { label: 'Booking Request Declined',   category: 'transactional', trigger: 'when a booking request is rejected', vars: ['Customer name','Salon & location','Date','Time'] },
  payment_received:      { label: 'Payment Received 🙏',        category: 'transactional', trigger: 'when a transaction is marked Completed', vars: ['Customer name','Amount ($)','Invoice number','Salon & location'] },
  loyalty_points_earned: { label: 'Loyalty Points Earned 🌟',   category: 'transactional', trigger: 'when loyalty points are awarded', vars: ['Customer name','Points earned','Salon & location','Total balance'] },
  refer_a_friend:        { label: 'Refer a Friend 👯‍♀️',         category: 'promotional',   vars: ['Customer name','Salon & location','Discount amount ($)','Referral code'] },
  weekend_special:       { label: 'Weekend Special 🎨',         category: 'promotional',   vars: ['Customer name','Salon & location','Promotion description'] },
  birthday_special:      { label: 'Birthday Special 🎂',        category: 'promotional',   vars: ['Customer name','Salon & location','Promotion description'] },
  promo_general:         { label: 'General Promotion',          category: 'promotional',   vars: ['Customer name','Promotion message','Salon name'] },
};

// Vars resolved automatically from customer/salon data at send time
const AUTO_FILLED_VARS = new Set([
  'Customer name','Salon & location','Date','Time','Service','Stylist','Appointment time','Salon name',
  'Amount ($)','Invoice number','Points earned','Total balance'
]);

// Formats matching the examples Meta approved the templates with ("Monday, 14 October 2026" / "2:30 PM"),
// always in the salon's timezone rather than the admin's browser
const WA_TZ = 'Asia/Singapore';
const toWaDate = (v) => (v ? (v.toDate ? v.toDate() : new Date(v)) : null);
const formatWaDate = (d) => {
  const p = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: WA_TZ })
    .formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  return `${p.weekday}, ${p.day} ${p.month} ${p.year}`;
};
const formatWaTime = (d) =>
  d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: WA_TZ }).replace(/[\u202f\u00a0]/g, ' ');

function WhatsAppCampaignsEditor({ salonId, onUpdate, prefillBody, onPrefillConsumed, prefillCampaign, onCampaignPrefillConsumed }) {
  const { currentUser } = useSalonAuth();
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [whatsappVerified, setWhatsappVerified] = useState(false);

  const [whatsappErrors, setWhatsappErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const [targetingCriteriaSaved, setTargetingCriteriaSaved] = useState(false);

  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const [campaignSettings, setCampaignSettings] = useState({});

  // Load which automatic WhatsApp templates are enabled for this salon
  useEffect(() => {
    if (!salonId) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'salons', salonId))
      .then((snap) => {
        if (!cancelled && snap.exists() && snap.data().transactionalCampaignSettings) {
          setCampaignSettings(snap.data().transactionalCampaignSettings);
        }
      })
      .catch((err) => console.error('Error loading WhatsApp campaign settings:', err));
    return () => { cancelled = true; };
  }, [salonId]);

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState('create-campaign');

  // Template state
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState('transactional');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customTemplates, setCustomTemplates] = useState({ transactional: [], promotional: [] });
  const [templateEditMode, setTemplateEditMode] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreateInfo, setShowCreateInfo] = useState(false);

  // Meta template state
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [metaTemplatesLoading, setMetaTemplatesLoading] = useState(false);
  const [metaTemplatesError, setMetaTemplatesError] = useState(null);
  const [selectedMetaTemplate, setSelectedMetaTemplate] = useState(null);
  const [templateVarValues, setTemplateVarValues] = useState([]);

  // State for appointment-based targeting and personalization
  const [salonServices, setSalonServices] = useState([]);
  const [salonStaff, setSalonStaff] = useState([]);
  const [loadingServicesStaff, setLoadingServicesStaff] = useState(false);
  const [salonPhone, setSalonPhone] = useState('');
  const [salonName, setSalonName] = useState('');
  const [industry, setIndustry] = useState('hair');
  useEffect(() => {
    if (!salonId) return;
    getSalonIndustry(salonId).then(setIndustry);
  }, [salonId]);

  // Image/GIF state
  const [imagePreview, setImagePreview] = useState('');
  const [imageError, setImageError] = useState('');
  const [previewDevice, setPreviewDevice] = useState('mobile');

  // WhatsApp specific state
  const [formData, setFormData] = useState({
    messageCategory: 'not_selected', // transactional or promotional
    messageType: 'not_selected', // For transactional: appointment_update, payment_update, loyalty_update
    templateName: '',
    templateLanguage: 'en',
    subject: '',
    body: '',
    imageUrl: '',
    schedulingType: 'one-time', // one-time or recurring
    scheduledDate: '',
    scheduledTime: '',
    conversionPeriod: 30,
    recurring: {
      frequency: 'daily', // daily, weekly, monthly, annually
      dayOfWeek: '', // for weekly
      dayOfMonth: 0, // for monthly (1-31)
      monthOfYear: '', // for annually
      dayOfYear: 0, // for annually (1-31)
      startDate: '',
      endDate: '',
      endType: 'date', // date or cycles
      numberOfCycles: 0,
      excludePreviousRecipients: false,
      exclusionType: 'cycles', // cycles or date
      excludePastCycles: 0,
      excludeBeforeDate: ''
    },
    targetingType: 'not_selected',
    targetingCriteria: {
      birthMonths: [],
      postalCodes: [],
      lastTransactionMonths: 0,
      lastTransactionUnit: 'months',
      transactionCount: 0,
      transactionType: 'not_selected',
      transactionAmountType: 'not_selected',
      transactionAmount: 0,
      transactionLocationId: 'not_selected',
      memberTiers: [],
      pointsType: 'not_selected',
      pointsAmount: 0,
      specificCustomers: [],
      notesSearchText: '', // #Revised UI_v2 — customer notes targeting
      notesMatchType: 'contains_all', // #Revised UI_v2 — customer notes targeting
      appointmentTimeValue: 0,
      appointmentTimeUnit: 'days',
      appointmentService: 'not_selected',
      appointmentStaff: 'not_selected',
      appointmentStatus: []
    }
  });

  // Appointment status options
  const appointmentStatusOptions = ['approved', 'pending', 'rejected', 'cancelled'];

  const birthMonthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const memberTierOptions = [
    'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'
  ];

  const commonEmojis = ['😊', '🎉', '💄', '✨', '💅', '💗', '🌟', '💖', '🔥', '💯', '👑', '💃'];

  // WhatsApp Campaign Templates
  const whatsappTemplates = {
    transactional: [
      {
        id: 'appointment_reminder',
        title: 'Appointment Reminder Tomorrow 🗓️',
        messageCategory: 'transactional',
        messageType: 'appointment_update',
        isAutoTriggered: true,
        subject: 'Appointment Reminder Tomorrow 🗓️',
        body: "Hi {name}! This is a reminder that you have an appointment tomorrow at {time} with our stylist {staff} for {service}. We look forward to seeing you! 💇‍♀️",
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'payment_confirmation',
        title: 'Payment Received - Thank You! 🙏',
        messageCategory: 'transactional',
        messageType: 'payment_update',
        isAutoTriggered: true,
        subject: 'Payment Received - Thank You! 🙏',
        body: "Thank you for your payment of ${amount}! Your payment has been successfully processed. Invoice #{invoice_number} has been sent to your email. See you at your next appointment! 💳✅",
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'loyalty_points',
        title: 'You Earned New Loyalty Points! 🌟',
        messageCategory: 'transactional',
        messageType: 'loyalty_update',
        isAutoTriggered: true,
        subject: 'You Earned New Loyalty Points! 🌟',
        body: "Great news! You've earned {points_earned} loyalty points from your recent visit. Your total balance is now {total_points} points! 🎁 You're only {points_to_reward} points away from your next reward. Keep collecting! ⭐",
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      }
    ],
    promotional: [
      {
        id: 'weekend_special',
        title: 'Weekend Special: 20% Off Hair Coloring! 🎨',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'Weekend Special: 20% Off Hair Coloring! 🎨',
        body: "🎉 Special Weekend Offer! Get 20% off all hair coloring services this Saturday & Sunday only! Book now by replying to this message or calling us at {phone}. Limited slots available! ✨💄",
        targetingType: 'customer_points',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 6,
          lastTransactionUnit: 'months',
          transactionCount: 2,
          transactionType: 'greater_than',
          transactionAmountType: 'greater_than',
          transactionAmount: 100,
          memberTiers: ['Gold', 'Platinum'],
          pointsType: 'greater_than',
          pointsAmount: 500,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'birthday_special',
        title: 'Happy Birthday! Special Gift Inside 🎂',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'Happy Birthday! Special Gift Inside 🎂',
        body: "🎂 Happy Birthday Month! As our valued customer, enjoy a special birthday treat: 30% off any service of your choice! Valid throughout your birthday month. Book your celebration appointment today! 🎉🎁",
        targetingType: 'customer_profile',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'referral_program',
        title: 'Refer a Friend & Get Rewarded! 👯‍♀️',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'Refer a Friend & Get Rewarded! 👯‍♀️',
        body: "Love our services? Share the love! 💗 Refer a friend and you both get $20 off your next visit! Plus, earn bonus loyalty points. The more you refer, the more you save! Start referring today! 🎁",
        targetingType: 'customer_points',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 3,
          lastTransactionUnit: 'months',
          transactionCount: 3,
          transactionType: 'greater_than',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: ['Gold', 'Platinum', 'Diamond'],
          pointsType: 'greater_than',
          pointsAmount: 300,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      }
    ]
  };

  // Load custom templates from localStorage and campaign settings from Firestore on mount
  useEffect(() => {
    const savedTemplates = localStorage.getItem(`whatsapp_templates_${salonId}`);
    if (savedTemplates) {
      try {
        setCustomTemplates(JSON.parse(savedTemplates));
      } catch (err) {
        console.error('Error loading templates:', err);
      }
    }

    // Load transactional campaign settings from Firestore
    const loadCampaignSettings = async () => {
      if (!salonId) return;
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const salonDoc = await getDoc(doc(db, 'salons', salonId));
        if (salonDoc.exists()) {
          const salonData = salonDoc.data();
          if (salonData.transactionalCampaignSettings) {
            setCampaignSettings(salonData.transactionalCampaignSettings);
          }
        }
      } catch (err) {
        console.error('Error loading campaign settings:', err);
      }
    };

    loadCampaignSettings();
  }, [salonId]);

  // Fetch approved Meta templates from backend
  const fetchMetaTemplates = useCallback(async (refresh = false) => {
    if (!salonId) return;
    setMetaTemplatesLoading(true);
    setMetaTemplatesError(null);
    try {
      const result = await whatsappService.getMessageTemplates(salonId, refresh);
      if (result.success && Array.isArray(result.data)) {
        setMetaTemplates(result.data);
      } else {
        setMetaTemplatesError(result.message || 'Unexpected response from server');
      }
    } catch (err) {
      console.error('Error fetching Meta templates:', err);
      setMetaTemplatesError(err.message || 'Failed to load templates');
    } finally {
      setMetaTemplatesLoading(false);
    }
  }, [salonId]);

  useEffect(() => {
    fetchMetaTemplates();
  }, [fetchMetaTemplates]);

  // Check WhatsApp Business API configuration status
  useEffect(() => {
    const checkWhatsAppConfig = async () => {
      if (!salonId) return;
      try {
        const result = await whatsappService.verifyConfiguration(salonId);
        if (result.success && result.data) {
          setWhatsappConfigured(result.data.isConfigured || false);
          setWhatsappVerified(result.data.isVerified || false);
        }
      } catch (err) {
        console.error('Error checking WhatsApp config:', err);
        // Non-blocking — campaign editor still works for drafting
      }
    };
    checkWhatsAppConfig();
  }, [salonId]);

  // Load services, staff, and salon phone for appointment-based targeting and personalization
  useEffect(() => {
    const loadServicesStaffAndPhone = async () => {
      if (!salonId) return;

      setLoadingServicesStaff(true);
      try {
        const [services, staff, phone, name] = await Promise.all([
          getSalonServices(salonId),
          getSalonStaff(salonId),
          getSalonPhone(salonId),
          getSalonName(salonId)
        ]);

        setSalonServices(services.filter(s => s.isActive !== false));
        setSalonStaff(staff.filter(s => s.isActive !== false));
        setSalonPhone(phone || '');
        setSalonName(name || '');
      } catch (err) {
        console.error('Error loading services, staff, and phone:', err);
      } finally {
        setLoadingServicesStaff(false);
      }
    };

    loadServicesStaffAndPhone();
  }, [salonId]);

  // Apply AI-generated prefill when provided
  useEffect(() => {
    if (!prefillBody) return;
    setFormData(prev => ({ ...prev, body: prefillBody, messageCategory: 'promotional' }));
    setActiveSubTab('create-campaign');
    window.scrollTo(0, 0);
    if (onPrefillConsumed) onPrefillConsumed();
  }, [prefillBody]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore a failed campaign for editing — user must choose a new send time.
  // Also handles pre-population from Find Prospects (specific_customer targeting).
  useEffect(() => {
    if (!prefillCampaign) return;
    const prefillSpecificCustomers = prefillCampaign.targetingCriteria?.specificCustomers || [];
    const hasSpecificCustomers =
      prefillCampaign.targetingType === 'specific_customer' && prefillSpecificCustomers.length > 0;
    setFormData(prev => ({
      ...prev,
      subject: prefillCampaign.subject || '',
      body: prefillCampaign.body || '',
      imageUrl: prefillCampaign.imageUrl || '',
      messageCategory: prefillCampaign.messageCategory || 'not_selected',
      messageType: prefillCampaign.messageType || 'not_selected',
      scheduledDate: '',
      scheduledTime: '',
      schedulingType: prefillCampaign.schedulingType || 'one-time',
      recurring: prefillCampaign.recurring
        ? { ...prev.recurring, ...prefillCampaign.recurring }
        : prev.recurring,
      targetingType: prefillCampaign.targetingType || 'not_selected',
      // Merge with defaults so every field has a defined value regardless of campaign age
      targetingCriteria: {
        ...prev.targetingCriteria,
        ...(prefillCampaign.targetingCriteria || {}),
      },
    }));
    // When coming from Find Prospects, pre-populate the customer chips and mark as saved
    if (hasSpecificCustomers) {
      setSelectedCustomers(prefillSpecificCustomers);
      setTargetingCriteriaSaved(true);
    } else {
      setSelectedCustomers([]);
      setTargetingCriteriaSaved(false);
    }
    setActiveSubTab('create-campaign');
    window.scrollTo(0, 0);
    if (onCampaignPrefillConsumed) onCampaignPrefillConsumed();
  }, [prefillCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle transactional campaign enabled/disabled and save to Firebase
  const handleToggleCampaign = async (template) => {
    const key = `whatsapp_${template.id}`;
    const currentlyEnabled = campaignSettings[key] !== false;
    const newSettings = { ...campaignSettings, [key]: !currentlyEnabled };
    setCampaignSettings(newSettings);

    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'salons', salonId), {
        [`transactionalCampaignSettings.${key}`]: !currentlyEnabled
      });
      console.log(`WhatsApp campaign ${template.id} ${!currentlyEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Error toggling WhatsApp campaign:', err);
      setCampaignSettings(campaignSettings);
    }
  };

  // Render the approved Meta template body with sample values for the Message Preview.
  // Typed values win; otherwise Meta's example values (as in WhatsApp Manager); otherwise [Variable name].
  const renderMetaTemplateBody = (template, varValues) => {
    const bodyComp = (template?.components || []).find(c => c.type === 'BODY');
    if (!bodyComp?.text) return null;
    const examples = bodyComp.example?.body_text?.[0] || [];
    return bodyComp.text.replace(/\{\{(\d+)\}\}/g, (_, n) => {
      const idx = parseInt(n, 10) - 1;
      const typed = varValues[idx]?.value;
      if (typed && typed.trim()) return typed.trim();
      if (examples[idx]) return examples[idx];
      return `[${varValues[idx]?.varName || `Variable ${n}`}]`;
    });
  };

  // Keep the preview body in sync with the selected template and its variable values
  useEffect(() => {
    if (!selectedMetaTemplate) return;
    const rendered = renderMetaTemplateBody(selectedMetaTemplate, templateVarValues);
    if (rendered) setFormData(prev => (prev.body === rendered ? prev : { ...prev, body: rendered }));
  }, [selectedMetaTemplate, templateVarValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Select a Meta template and initialise var values
  const handleSelectMetaTemplate = (template) => {
    const meta = TEMPLATE_META[template.name];
    if (!meta) return;
    setSelectedMetaTemplate(template);
    const vars = meta.vars.map((varName) => ({
      varName,
      isAuto: AUTO_FILLED_VARS.has(varName),
      value: AUTO_FILLED_VARS.has(varName) ? '' : ''
    }));
    setTemplateVarValues(vars);
    setFormData(prev => ({
      ...prev,
      templateName: template.name,
      templateLanguage: 'en',
      messageCategory: meta.category === 'transactional' ? 'transactional' : 'promotional',
      subject: meta.label,
      body: `[Template: ${template.name}]`
    }));
    setFieldErrors({});
  };

  // Build Meta template components array from var values and customer data
  const buildTemplateComponents = (varValues, customer, appointmentData) => {
    // Meta rejects empty parameters (#131008), so every value needs a non-blank fallback
    const text = (value, fallback) => ({ type: 'text', text: (value && String(value).trim()) || fallback });
    const params = varValues.map((v) => {
      if (!v.isAuto) return text(v.value, '-');
      switch (v.varName) {
        case 'Customer name': return text(customer?.displayName || customer?.name, 'Valued Customer');
        case 'Salon & location': return text(appointmentData?.salonLabel || salonName, 'Our Salon');
        case 'Salon name': return text(salonName, 'Our Salon');
        case 'Date': return text(appointmentData?.date, new Date().toLocaleDateString());
        case 'Time': return text(appointmentData?.time, 'your scheduled time');
        case 'Appointment time': return text(appointmentData?.time, 'your scheduled time');
        case 'Service': return text(appointmentData?.serviceName, 'your service');
        case 'Stylist': return text(appointmentData?.staffName, 'our team');
        case 'Amount ($)': return text(appointmentData?.amount, '-');
        case 'Invoice number': return text(appointmentData?.invoiceNumber, '-');
        case 'Points earned': return text(appointmentData?.pointsEarned, '-');
        case 'Total balance': return text(appointmentData?.totalBalance, '-');
        default: return text(v.value, '-');
      }
    });
    return [{ type: 'body', parameters: params }];
  };

  // Save custom templates to localStorage
  const saveTemplatesToStorage = (templates) => {
    localStorage.setItem(`whatsapp_templates_${salonId}`, JSON.stringify(templates));
    setCustomTemplates(templates);
  };

  // Get all templates (default + custom); custom entries override defaults with the same id
  const getAllTemplates = (category) => {
    const custom = customTemplates[category] || [];
    const customIds = new Set(custom.map(t => t.id));
    const defaults = applyIndustryContent('whatsapp', whatsappTemplates[category], industry).filter(t => !customIds.has(t.id));
    return [...defaults, ...custom];
  };

  // Function to load template into form
  const handleLoadTemplate = (template) => {
    setFormData({
      messageCategory: template.messageCategory,
      messageType: template.messageType,
      subject: template.subject,
      body: template.body,
      imageUrl: '',
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: template.conversionPeriod ?? 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: template.targetingType,
      targetingCriteria: template.targetingCriteria
    });
    setSelectedCustomers([]);
    setTargetingCriteriaSaved(false);
    setSelectedTemplate(null);
    setTemplateEditMode(false);
    setTemplateToEdit(null);
    setFieldErrors({});
    setSuccess('Template loaded successfully! Please fill in the scheduling details and customize as needed.');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to the form
    const formElement = document.querySelector('.notification-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to revise template
  const handleReviseTemplate = (template) => {
    setFormData({
      messageCategory: template.messageCategory,
      messageType: template.messageType,
      subject: template.subject,
      body: template.body,
      imageUrl: '',
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: template.conversionPeriod ?? 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: template.targetingType,
      targetingCriteria: template.targetingCriteria
    });
    setSelectedCustomers([]);
    setTargetingCriteriaSaved(false);
    setTemplateEditMode(true);
    setTemplateToEdit(template);
    setSelectedTemplate(null);
    setFieldErrors({});
    setSuccess('Template loaded for editing. Make your changes and click "Make Revisions to Template" to save.');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to the form
    const formElement = document.querySelector('.notification-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to save revised template
  const handleSaveRevisedTemplate = () => {
    if (!templateToEdit) return;

    // Validation
    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Please fill in the title and message body');
      setTimeout(() => setError(''), 5000);
      return;
    }

    const category = templateToEdit.messageCategory;
    const updatedCustomTemplates = { ...customTemplates, [category]: [...(customTemplates[category] || [])] };

    const revised = {
      ...templateToEdit,
      title: formData.subject,
      subject: formData.subject,
      body: formData.body,
      messageType: formData.messageType,
      targetingType: formData.targetingType,
      targetingCriteria: formData.targetingCriteria,
      isCustom: true
    };

    const templateIndex = updatedCustomTemplates[category].findIndex(t => t.id === templateToEdit.id);
    if (templateIndex !== -1) {
      updatedCustomTemplates[category][templateIndex] = revised;
    } else {
      // Default template being revised — save as custom override with same id
      updatedCustomTemplates[category].push(revised);
    }

    saveTemplatesToStorage(updatedCustomTemplates);
    setTemplateEditMode(false);
    setTemplateToEdit(null);
    resetWhatsAppForm();
    setSuccess('Template revised successfully!');
    setTimeout(() => setSuccess(''), 5000);
  };

  // Function to create new template
  const handleCreateTemplate = () => {
    // Validation
    if (formData.messageCategory === 'not_selected') {
      setError('Please select a message category');
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Please fill in the title and message body');
      setTimeout(() => setError(''), 5000);
      return;
    }

    const category = formData.messageCategory;
    const newTemplate = {
      id: `custom_${Date.now()}`,
      title: formData.subject,
      messageCategory: formData.messageCategory,
      messageType: formData.messageType,
      subject: formData.subject,
      body: formData.body,
      targetingType: formData.targetingType,
      targetingCriteria: formData.targetingCriteria,
      isCustom: true
    };

    const updatedCustomTemplates = { ...customTemplates };
    updatedCustomTemplates[category] = [...(updatedCustomTemplates[category] || []), newTemplate];

    saveTemplatesToStorage(updatedCustomTemplates);
    resetWhatsAppForm();
    setSuccess('Template created successfully!');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to templates section
    const templatesElement = document.querySelector('.templates-section');
    if (templatesElement) {
      templatesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to delete template
  const handleDeleteTemplate = () => {
    if (!selectedTemplate || !selectedTemplate.isCustom) {
      setError('You can only delete custom templates');
      setTimeout(() => setError(''), 5000);
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmDeleteTemplate = () => {
    const category = selectedTemplate.messageCategory;
    const updatedCustomTemplates = { ...customTemplates };

    updatedCustomTemplates[category] = updatedCustomTemplates[category].filter(
      t => t.id !== selectedTemplate.id
    );

    saveTemplatesToStorage(updatedCustomTemplates);
    setSelectedTemplate(null);
    setShowDeleteConfirm(false);
    setSuccess('Template deleted successfully!');
    setTimeout(() => setSuccess(''), 5000);
  };

  const handlePreviewTargeting = async () => {
    try {
      setPreviewLoading(true);
      setError('');
      const preview = await getCustomerTargetingPreview(salonId, {
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria
      });
      setPreviewData(preview);
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Error getting preview:', err);
      setError('Failed to get targeting preview. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadSocialCSV = async () => {
    try {
      const preview = await getCustomerTargetingPreview(salonId, {
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria
      });
      const customers = preview.eligibleCustomers || [];
      const rows = [['first_name', 'last_name', 'email', 'phone']];
      customers.forEach(c => {
        const nameParts = (c.displayName || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const email = (c.email || '').toLowerCase();
        const digitsOnly = (c.phone || '').replace(/\D/g, '');
        const phone = digitsOnly.length === 8 ? `65${digitsOnly}` : digitsOnly;
        rows.push([firstName, lastName, email, phone]);
      });
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'social_platform_audience.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading social CSV:', err);
      setError('Failed to generate CSV. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  };

  const searchCustomers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    setSearchingCustomers(true);
    try {
      const customersRef = collection(db, 'customers');
      const searchLower = searchTerm.toLowerCase();

      const emailQuery = query(
        customersRef,
        where('salonId', '==', salonId),
        where('email', '>=', searchLower),
        where('email', '<=', searchLower + ''),
        limit(20)
      );

      const nameQuery = query(
        customersRef,
        where('salonId', '==', salonId),
        where('displayName', '>=', searchTerm),
        where('displayName', '<=', searchTerm + ''),
        limit(20)
      );

      const [emailSnapshot, nameSnapshot] = await Promise.all([
        getDocs(emailQuery),
        getDocs(nameQuery)
      ]);

      const customerMap = new Map();

      const addFromSnapshot = (snapshot) => {
        snapshot.forEach((doc) => {
          if (!customerMap.has(doc.id)) {
            const customerData = doc.data();
            customerMap.set(doc.id, {
              id: doc.id,
              email: customerData.email || null,
              displayName: customerData.displayName || null,
              phoneNumber: customerData.phoneNumber || null,
              uid: customerData.uid || null
            });
          }
        });
      };

      addFromSnapshot(emailSnapshot);
      addFromSnapshot(nameSnapshot);

      const customers = Array.from(customerMap.values());

      const uniqueCustomers = customers.filter((customer, index, self) =>
        index === self.findIndex(c => c.id === customer.id) &&
        !selectedCustomers.some(selected => selected.id === customer.id)
      );

      setCustomerSearchResults(uniqueCustomers);
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearchQuery(value);
    setShowCustomerDropdown(true);

    const timeoutId = setTimeout(() => {
      searchCustomers(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const handleCustomerSelect = (customer) => {
    const updatedCustomers = [...selectedCustomers, customer];
    setSelectedCustomers(updatedCustomers);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);

    handleTargetingCriteriaChange('specificCustomers', updatedCustomers);
  };

  const handleCustomerRemove = (customerId) => {
    const updatedCustomers = selectedCustomers.filter(customer => customer.id !== customerId);
    setSelectedCustomers(updatedCustomers);
    handleTargetingCriteriaChange('specificCustomers', updatedCustomers);
  };

  const validateField = (fieldName, value) => {
    let isValid = true;
    let errorMessage = '';

    switch (fieldName) {
      case 'messageCategory':
        if (value === 'not_selected') {
          errorMessage = 'Message category is required';
          isValid = false;
        }
        break;
      case 'messageType':
        if (formData.messageCategory === 'transactional' && value === 'not_selected') {
          errorMessage = 'Message type is required for transactional messages';
          isValid = false;
        }
        break;
      case 'subject':
        if (!value || !value.trim()) {
          errorMessage = 'Message title is required';
          isValid = false;
        }
        break;
      case 'body':
        if (!value || !value.trim()) {
          errorMessage = 'Message content is required';
          isValid = false;
        }
        break;
      case 'scheduledDate':
        if (formData.schedulingType === 'one-time' && !value) {
          errorMessage = 'Scheduled date is required';
          isValid = false;
        }
        break;
      case 'scheduledTime':
        if (!value) {
          errorMessage = 'Scheduled time is required';
          isValid = false;
        }
        break;
      case 'targetingType':
        if (value === 'not_selected') {
          errorMessage = 'Target audience is required';
          isValid = false;
        }
        break;
      default:
        break;
    }

    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: errorMessage
    }));

    return isValid;
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const validateWhatsAppCampaign = () => {
    const errors = {};

    if (formData.messageCategory === 'promotional' && !formData.templateName) {
      const leftovers = [...findUnresolvedPlaceholders(formData.subject, { promotional: true }), ...findUnresolvedPlaceholders(formData.body, { promotional: true })];
      if (leftovers.length) errors.body = `Replace these placeholders before sending: ${[...new Set(leftovers)].join(', ')}`;
    }

    if (formData.messageCategory === 'not_selected') {
      errors.messageCategory = 'Message category is required';
    }

    if (formData.messageCategory === 'transactional' && formData.messageType === 'not_selected') {
      errors.messageType = 'Message type is required for transactional messages';
    }

    if (!formData.subject.trim()) {
      errors.subject = 'Message title is required';
    }

    if (!formData.templateName && !formData.body.trim()) {
      errors.body = 'Message content is required';
    }

    if (formData.templateName && selectedMetaTemplate) {
      const meta = TEMPLATE_META[formData.templateName];
      if (meta) {
        const missingManual = templateVarValues.some(v => !v.isAuto && !v.value.trim());
        if (missingManual) {
          errors.templateVars = 'Please fill in all required template variable fields';
        }
      }
    }

    if (!formData.scheduledTime) {
      errors.scheduledTime = 'Scheduled time is required';
    }

    // One-time campaign validation
    if (formData.schedulingType === 'one-time') {
      if (!formData.scheduledDate) {
        errors.scheduledDate = 'Scheduled date is required';
      }

      if (formData.scheduledDate && formData.scheduledTime) {
        const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
        const now = new Date();
        if (scheduledDateTime <= now) {
          errors.scheduledDateTime = 'Scheduled date and time must be in the future';
        }
      }
    }

    // Recurring campaign validation
    if (formData.schedulingType === 'recurring') {
      if (!formData.recurring.startDate) {
        errors.recurringStartDate = 'Start date is required for recurring campaigns';
      }

      if (formData.recurring.endType === 'date' && !formData.recurring.endDate) {
        errors.recurringEndDate = 'End date is required';
      }

      if (formData.recurring.endType === 'date' && formData.recurring.startDate && formData.recurring.endDate) {
        if (new Date(formData.recurring.endDate) <= new Date(formData.recurring.startDate)) {
          errors.recurringEndDate = 'End date must be after start date';
        }
      }

      if (formData.recurring.endType === 'cycles' && formData.recurring.numberOfCycles < 1) {
        errors.numberOfCycles = 'Number of cycles must be at least 1';
      }

      if (formData.recurring.startDate) {
        const startDate = new Date(formData.recurring.startDate);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (startDate < now) {
          errors.recurringStartDate = 'Start date must be today or in the future';
        }
      }

      // Exclusion settings validation
      if (formData.recurring.excludePreviousRecipients) {
        if (formData.recurring.exclusionType === 'cycles' && formData.recurring.excludePastCycles < 1) {
          errors.excludePastCycles = 'Number of past cycles must be at least 1';
        }

        if (formData.recurring.exclusionType === 'date' && !formData.recurring.excludeBeforeDate) {
          errors.excludeBeforeDate = 'Exclusion cutoff date is required';
        }

        if (formData.recurring.exclusionType === 'date' && formData.recurring.excludeBeforeDate && formData.recurring.startDate) {
          if (new Date(formData.recurring.excludeBeforeDate) >= new Date(formData.recurring.startDate)) {
            errors.excludeBeforeDate = 'Exclusion cutoff date must be before campaign start date';
          }
        }
      }
    }

    if (formData.targetingType === 'specific_customer' && formData.targetingCriteria.specificCustomers.length === 0) {
      errors.specificCustomers = 'Please select at least one customer for specific customer targeting';
    }

    setWhatsappErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getAdminUsers = async () => {
    try {
      const adminQuery = query(
        collection(db, 'salonUsers'),
        where('salonId', '==', salonId)
      );

      const adminSnapshot = await getDocs(adminQuery);
      const adminUsers = [];

      adminSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (['owner', 'admin', 'manager'].includes(userData.role)) {
          adminUsers.push(userData);
        }
      });

      console.log(`Found ${adminUsers.length} admin users for salon ${salonId}:`, adminUsers);
      return adminUsers;
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  };

  const showValidationErrors = (errors) => {
    const errorMessages = Object.values(errors).filter(msg => msg);
    if (errorMessages.length > 0) {
      alert(`Please fix the following errors:\n\n• ${errorMessages.join('\n• ')}`);
    }
  };

  const handleSaveTargetingCriteria = () => {
    setTargetingCriteriaSaved(true);
    setSuccess('Targeting criteria saved successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const getTargetedCustomers = async () => {
    try {
      console.log('Getting targeted customers for salonId:', salonId);

      const customersRef = collection(db, 'customers');
      const customersQuery = query(
        customersRef,
        where('salonId', '==', salonId)
      );

      const customersSnapshot = await getDocs(customersQuery);
      let customers = [];

      customersSnapshot.forEach((doc) => {
        const customerData = doc.data();
        customers.push({
          id: doc.id,
          ...customerData
        });
      });

      console.log(`Found ${customers.length} total customers for salon ${salonId}`);

      // Filter customers who have WhatsApp enabled and have phone numbers
      customers = customers.filter(customer =>
        customer.preferences?.notifications?.whatsapp === true &&
        customer.phoneNumber
      );

      console.log(`${customers.length} customers have WhatsApp enabled and phone numbers`);

      let targetedCustomers = customers;

      if (formData.targetingType === 'customer_profile') {
        targetedCustomers = customers.filter(customer => {
          if (formData.targetingCriteria.birthMonths.length > 0) {
            if (!formData.targetingCriteria.birthMonths.includes(customer.birthMonth)) {
              return false;
            }
          }

          if (formData.targetingCriteria.postalCodes.length > 0) {
            const customerPostal = customer.postalCode || '';
            const matchesPostal = formData.targetingCriteria.postalCodes.some(code =>
              customerPostal.startsWith(code)
            );
            if (!matchesPostal) {
              return false;
            }
          }

          return true;
        });
      }
      else if (formData.targetingType === 'customer_transaction') {
        const txLocId = formData.targetingCriteria.transactionLocationId;
        targetedCustomers = customers.filter(customer => {
          // Location filter: if a specific location is chosen, match by customer's locationId
          if (txLocId && txLocId !== 'not_selected') {
            if (customer.locationId !== txLocId) return false;
          }

          const totalVisits = customer.metrics?.totalVisits || 0;
          const totalSpent = customer.metrics?.totalSpent || 0;
          const lastTransaction = customer.metrics?.lastTransaction;

          const transactionCount = formData.targetingCriteria.transactionCount;
          const transactionType = formData.targetingCriteria.transactionType;

          if (transactionType === 'less_than' && totalVisits >= transactionCount) {
            return false;
          }
          if (transactionType === 'equal_to' && totalVisits !== transactionCount) {
            return false;
          }
          if (transactionType === 'greater_than' && totalVisits <= transactionCount) {
            return false;
          }

          const transactionAmount = formData.targetingCriteria.transactionAmount;
          const transactionAmountType = formData.targetingCriteria.transactionAmountType;

          if (transactionAmountType === 'greater_than' && totalSpent <= transactionAmount) {
            return false;
          }
          if (transactionAmountType === 'less_than' && totalSpent >= transactionAmount) {
            return false;
          }

          if (lastTransaction) {
            const lastTransactionDate = lastTransaction.toDate ? lastTransaction.toDate() : new Date(lastTransaction);
            const timeValue = formData.targetingCriteria.lastTransactionMonths;
            const timeUnit = formData.targetingCriteria.lastTransactionUnit || 'months';
            const cutoffDate = new Date();

            switch (timeUnit) {
              case 'hours':
                cutoffDate.setHours(cutoffDate.getHours() - timeValue);
                break;
              case 'days':
                cutoffDate.setDate(cutoffDate.getDate() - timeValue);
                break;
              case 'months':
                cutoffDate.setMonth(cutoffDate.getMonth() - timeValue);
                break;
              case 'years':
                cutoffDate.setFullYear(cutoffDate.getFullYear() - timeValue);
                break;
              default:
                cutoffDate.setMonth(cutoffDate.getMonth() - timeValue);
            }

            if (lastTransactionDate < cutoffDate) {
              return false;
            }
          }

          return true;
        });
      }
      else if (formData.targetingType === 'customer_points') {
        targetedCustomers = customers.filter(customer => {
          if (formData.targetingCriteria.memberTiers.length > 0) {
            if (!formData.targetingCriteria.memberTiers.includes(customer.loyaltyTier)) {
              return false;
            }
          }

          const pointsBalance = customer.metrics?.pointsBalance || 0;
          const pointsAmount = formData.targetingCriteria.pointsAmount;
          const pointsType = formData.targetingCriteria.pointsType;

          if (pointsType === 'greater_than' && pointsBalance <= pointsAmount) {
            return false;
          }
          if (pointsType === 'less_than' && pointsBalance >= pointsAmount) {
            return false;
          }

          return true;
        });
      }
      else if (formData.targetingType === 'customer_appointment') {
        // Query appointments collection based on criteria
        const appointmentsRef = collection(db, 'appointments');
        const appointmentsSnapshot = await getDocs(appointmentsRef);

        // Calculate the cutoff date for upcoming appointments
        const now = new Date();
        const cutoffDate = new Date();
        const timeValue = formData.targetingCriteria.appointmentTimeValue;
        const timeUnit = formData.targetingCriteria.appointmentTimeUnit;

        switch (timeUnit) {
          case 'hours':
            cutoffDate.setHours(cutoffDate.getHours() + timeValue);
            break;
          case 'days':
            cutoffDate.setDate(cutoffDate.getDate() + timeValue);
            break;
          case 'months':
            cutoffDate.setMonth(cutoffDate.getMonth() + timeValue);
            break;
          default:
            cutoffDate.setDate(cutoffDate.getDate() + timeValue);
        }

        // Filter appointments based on criteria
        const matchingCustomerIds = new Set();

        appointmentsSnapshot.forEach((doc) => {
          const appointment = doc.data();

          // Check if appointment belongs to this salon
          if (appointment.salonId !== salonId) return;

          // Check dateTimeRequested is within the range
          const appointmentDate = appointment.dateTimeRequested?.toDate
            ? appointment.dateTimeRequested.toDate()
            : new Date(appointment.dateTimeRequested);

          if (appointmentDate < now || appointmentDate > cutoffDate) return;

          // Check service filter
          if (formData.targetingCriteria.appointmentService !== 'not_selected') {
            if (appointment.serviceId !== formData.targetingCriteria.appointmentService &&
                appointment.service !== formData.targetingCriteria.appointmentService) return;
          }

          // Check staff filter
          if (formData.targetingCriteria.appointmentStaff !== 'not_selected') {
            if (appointment.staffId !== formData.targetingCriteria.appointmentStaff &&
                appointment.staff !== formData.targetingCriteria.appointmentStaff) return;
          }

          // Check status filter
          if (formData.targetingCriteria.appointmentStatus.length > 0) {
            if (!formData.targetingCriteria.appointmentStatus.includes(appointment.status)) return;
          }

          // Add customer ID to the matching set
          if (appointment.customerId) {
            matchingCustomerIds.add(appointment.customerId);
          }
        });

        // Filter customers based on matching appointment customer IDs
        targetedCustomers = customers.filter(customer =>
          matchingCustomerIds.has(customer.id)
        );
      }
      else if (formData.targetingType === 'specific_customer') {
        const selectedCustomerIds = formData.targetingCriteria.specificCustomers.map(c => c.id);
        targetedCustomers = customers.filter(customer =>
          selectedCustomerIds.includes(customer.id)
        );
      }
      else if (formData.targetingType === 'customer_notes') { // #Revised UI_v2
        const searchText = (formData.targetingCriteria.notesSearchText || '').trim().toLowerCase();
        if (searchText) {
          const matchType = formData.targetingCriteria.notesMatchType || 'contains_all';
          targetedCustomers = customers.filter(customer => {
            const notes = (customer.customer_notes || '').toLowerCase();
            if (!notes) return false;
            if (matchType === 'exact') return notes.includes(searchText);
            const words = searchText.split(/[\s,]+/).filter(w => w.length > 0);
            if (matchType === 'contains_any') return words.some(word => notes.includes(word));
            return words.every(word => notes.includes(word)); // contains_all
          });
        }
      }

      // Return full customer objects with phone numbers for personalization
      const customersWithPhone = targetedCustomers.filter(customer => customer.phoneNumber);

      console.log(`Targeted ${customersWithPhone.length} customers with phone numbers after filtering`);

      return customersWithPhone;
    } catch (error) {
      console.error('Error getting targeted customers:', error);
      throw error;
    }
  };

  const handleSendTestWhatsAppBlast = async () => {
    if (!validateWhatsAppCampaign()) {
      showValidationErrors(whatsappErrors);
      return;
    }

    try {
      setSaving(true);
      setError('');

      const adminUsers = await getAdminUsers();

      if (adminUsers.length === 0) {
        throw new Error('No admin users found for this salon');
      }

      let scheduledFor;
      if (formData.schedulingType === 'one-time') {
        scheduledFor = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      } else {
        scheduledFor = new Date(`${formData.recurring.startDate}T${formData.scheduledTime}`);
      }

      // Check if message contains personalization placeholders
      const hasPersonalization = formData.subject.includes('{') || formData.body.includes('{');

      // For test, create sample customer data using admin info
      const sampleCustomer = {
        id: 'test-customer',
        displayName: adminUsers[0]?.displayName || adminUsers[0]?.name || adminUsers[0]?.email?.split('@')[0] || 'Test Customer',
        email: adminUsers[0]?.email,
        phoneNumber: adminUsers[0]?.phoneNumber,
        metrics: {
          pointsBalance: 250,
          totalVisits: 5,
          totalSpent: 500
        }
      };

      const sampleAppointment = {
        dateTimeRequested: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        serviceId: salonServices[0]?.id,
        staffId: salonStaff[0]?.id
      };

      const sampleTransaction = {
        totalAmount: 150,
        pointsEarned: 15
      };

      let personalizedTitle = formData.subject.trim();
      let personalizedBody = formData.body.trim();

      if (hasPersonalization) {
        personalizedTitle = await personalizeMessage(formData.subject, sampleCustomer, {
            promotional: formData.messageCategory === 'promotional',
          salonId,
          salonName,
          appointment: sampleAppointment,
          transaction: sampleTransaction,
          salonPhone,
          services: salonServices,
          staffList: salonStaff
        });

        personalizedBody = await personalizeMessage(formData.body, sampleCustomer, {
            promotional: formData.messageCategory === 'promotional',
          salonId,
          salonName,
          appointment: sampleAppointment,
          transaction: sampleTransaction,
          salonPhone,
          services: salonServices,
          staffList: salonStaff
        });
      }

      // Build template components for test (use sample customer data)
      let templateComponents;
      if (formData.templateName && selectedMetaTemplate) {
        const sampleAppointmentResolved = {
          date: formatWaDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
          time: '2:00 PM',
          serviceName: salonServices[0]?.name || 'Hair Service',
          staffName: salonStaff[0]?.name || 'Stylist',
          amount: '150.00',
          invoiceNumber: 'INV-SAMPLE01',
          pointsEarned: '15',
          totalBalance: '250'
        };
        templateComponents = buildTemplateComponents(templateVarValues, sampleCustomer, sampleAppointmentResolved);
      }

      const campaignData = {
        body: personalizedBody,
        imageUrl: formData.imageUrl.trim() || null,
        messageCategory: formData.messageCategory,
        messageType: formData.messageType,
        templateName: formData.templateName || null,
        templateLanguage: formData.templateLanguage || 'en',
        templateComponents: templateComponents || null,
        createdAt: new Date(),
        failureCount: 0,
        schedulingType: formData.schedulingType,
        recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
        metadata: {
          campaignType: 'test_blast',
          createdBy: currentUser?.uid || '',
          createdByEmail: currentUser?.email || '',
          createdByName: currentUser?.displayName || currentUser?.name || 'Admin User',
          salonId: salonId,
          scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
          scheduledTime: formData.scheduledTime,
          schedulingType: formData.schedulingType,
          recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
          targetingCriteria: formData.targetingCriteria,
          targetingType: formData.targetingType,
          timestamp: new Date().toISOString(),
          isPersonalizationDemo: hasPersonalization
        },
        salonId: salonId,
        scheduledFor: scheduledFor,
        sentAt: new Date(),
        status: 'sent',
        successCount: adminUsers.length,
        targetRecipients: adminUsers.map(user => ({
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
          name: user.name || user.displayName || 'Admin User',
          role: user.role || 'admin',
          type: 'admin',
          uid: user.uid || ''
        })),
        title: personalizedTitle,
        type: 'whatsapp'
      };

      console.log('Sending test WhatsApp campaign:', campaignData);

      // Send via backend which calls Meta Cloud API
      const result = await whatsappService.sendTestMessage(campaignData, salonId);

      const personalizationNote = hasPersonalization ? ' (with sample personalization)' : '';
      if (whatsappConfigured && result.data?.successCount > 0) {
        setSuccess(`Test WhatsApp message sent to ${result.data.successCount} admin(s)${personalizationNote} via Meta WhatsApp API.`);
      } else {
        setSuccess(`Test WhatsApp campaign created for ${adminUsers.length} admin(s)${personalizationNote}. ${!whatsappConfigured ? 'Configure WhatsApp Business credentials in the WhatsApp Config tab to enable actual sending.' : ''}`)
      }
      setTimeout(() => setSuccess(''), 5000);

    } catch (err) {
      console.error('Error sending test WhatsApp blast:', err);
      setError('Failed to send test WhatsApp blast: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendActualWhatsAppBlast = async () => {
    if (!validateWhatsAppCampaign()) {
      showValidationErrors(whatsappErrors);
      return;
    }

    try {
      setSaving(true);
      setError('');

      let targetedCustomers = await getTargetedCustomers();

      if (targetedCustomers.length === 0) {
        throw new Error('No customers match the targeting criteria or all matching customers have WhatsApp disabled or no phone numbers');
      }

      // Apply frequency exclusion before sending
      const { eligible, excluded } = await applyFrequencyExclusionToList(salonId, 'whatsapp', targetedCustomers);
      targetedCustomers = eligible;
      if (targetedCustomers.length === 0) {
        throw new Error(`All ${excluded.length} targeted customer(s) have been excluded by frequency limits. Try again later or adjust your frequency settings.`);
      }

      let scheduledFor;
      if (formData.schedulingType === 'one-time') {
        scheduledFor = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      } else {
        scheduledFor = new Date(`${formData.recurring.startDate}T${formData.scheduledTime}`);
      }

      // Check if message contains personalization placeholders
      const hasPersonalization = formData.subject.includes('{') || formData.body.includes('{');

      // A Meta template body never contains a {placeholder}, so hasPersonalization is false for templates - but the
      // template variables (name, date, time, service, amount, ...) still differ per customer, so always resolve per customer.
      if (hasPersonalization || (formData.templateName && selectedMetaTemplate)) {
        // Create personalized WhatsApp messages for each customer
        const whatsappPromises = targetedCustomers.map(async (customer) => {
          // Get customer's latest appointment and transaction for personalization
          const [appointment, transaction] = await Promise.all([
            getCustomerAppointment(customer.id, salonId),
            getCustomerTransaction(customer.id, salonId)
          ]);

          // Personalize title and body
          const personalizedTitle = await personalizeMessage(formData.subject, customer, {
            promotional: formData.messageCategory === 'promotional',
            salonId,
            salonName,
            appointment,
            transaction,
            salonPhone,
            services: salonServices,
            staffList: salonStaff
          });

          const personalizedBody = await personalizeMessage(formData.body, customer, {
            promotional: formData.messageCategory === 'promotional',
            salonId,
            salonName,
            appointment,
            transaction,
            salonPhone,
            services: salonServices,
            staffList: salonStaff
          });

          // Build per-customer template components if using a Meta template
          let perCustomerTemplateComponents;
          if (formData.templateName && selectedMetaTemplate) {
            const apptStart = toWaDate(appointment?.dateTimeRequested);
            const apptLocation = appointment?.locationId ? locations.find(l => l.locationId === appointment.locationId) : null;
            const paid = parseFloat(transaction?.amount ?? transaction?.price);
            const apptResolved = {
              date: apptStart ? formatWaDate(apptStart) : '',
              time: apptStart ? formatWaTime(apptStart) : '',
              serviceName: salonServices.find(s => s.id === appointment?.serviceId)?.name || appointment?.serviceRequested?.name || appointment?.serviceName || '',
              staffName: salonStaff.find(s => s.id === appointment?.staffId)?.name || appointment?.staffName || '',
              salonLabel: isMultiLocation && apptLocation?.locationName ? `${salonName} - ${apptLocation.locationName}` : salonName,
              amount: Number.isFinite(paid) ? paid.toFixed(2) : '',
              invoiceNumber: transaction?.id ? `INV-${String(transaction.id).slice(-8).toUpperCase()}` : '',
              pointsEarned: transaction?.pointsEarned != null ? Number(transaction.pointsEarned).toLocaleString('en-US') : '',
              totalBalance: customer?.metrics?.pointsBalance != null ? Number(customer.metrics.pointsBalance).toLocaleString('en-US') : ''
            };
            perCustomerTemplateComponents = buildTemplateComponents(templateVarValues, customer, apptResolved);
          }

          const campaignData = {
            body: personalizedBody,
            imageUrl: formData.imageUrl.trim() || null,
            messageCategory: formData.messageCategory,
            messageType: formData.messageType,
            templateName: formData.templateName || null,
            templateLanguage: formData.templateLanguage || 'en',
            templateComponents: perCustomerTemplateComponents || null,
            createdAt: new Date(),
            failureCount: 0,
            schedulingType: formData.schedulingType,
            recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
            metadata: {
              campaignType: 'actual_blast',
              createdBy: currentUser?.uid || '',
              createdByEmail: currentUser?.email || '',
              createdByName: currentUser?.displayName || currentUser?.name || 'Admin User',
              salonId: salonId,
              scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
              scheduledTime: formData.scheduledTime,
              schedulingType: formData.schedulingType,
              recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
              targetingCriteria: formData.targetingCriteria,
              targetingType: formData.targetingType,
              timestamp: new Date().toISOString(),
              customerId: customer.id,
              customerName: customer.displayName,
              conversionPeriod: Number(formData.conversionPeriod) || 30
            },
            salonId: salonId,
            scheduledFor: scheduledFor,
            sentAt: null,
            status: 'scheduled',
            successCount: 0,
            targetRecipients: [{
              email: customer.email || '',
              phoneNumber: customer.phoneNumber || '',
              name: customer.displayName || customer.name || 'Customer',
              role: 'customer',
              type: 'customer',
              uid: customer.uid || customer.id || ''
            }],
            title: personalizedTitle,
            type: 'whatsapp'
          };

          // Schedule via backend (saves to Firestore)
          const result = await whatsappService.scheduleCampaign(campaignData, salonId);

          // If scheduled time is now (within next 2 minutes), immediately dispatch
          const twoMinutesFromNow = new Date(Date.now() + 2 * 60 * 1000);
          if (scheduledFor <= twoMinutesFromNow && result?.data?.campaignId) {
            await whatsappService.sendCampaign(result.data.campaignId, salonId);
          }

          return result;
        });

        await Promise.all(whatsappPromises);
      } else {
        // No personalization - send one WhatsApp message to all recipients
        const targetRecipients = targetedCustomers.map(customer => ({
          email: customer.email || '',
          phoneNumber: customer.phoneNumber || '',
          name: customer.displayName || customer.name || 'Customer',
          role: 'customer',
          type: 'customer',
          uid: customer.uid || customer.id || ''
        }));

        // For non-personalized template blast, build shared components (auto-vars use generic placeholders)
        let sharedTemplateComponents;
        if (formData.templateName && selectedMetaTemplate) {
          sharedTemplateComponents = buildTemplateComponents(templateVarValues, { displayName: 'Valued Customer' }, {});
        }

        const campaignData = {
          body: formData.body.trim(),
          imageUrl: formData.imageUrl.trim() || null,
          messageCategory: formData.messageCategory,
          messageType: formData.messageType,
          templateName: formData.templateName || null,
          templateLanguage: formData.templateLanguage || 'en',
          templateComponents: sharedTemplateComponents || null,
          createdAt: new Date(),
          failureCount: 0,
          schedulingType: formData.schedulingType,
          recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
          metadata: {
            campaignType: 'actual_blast',
            createdBy: currentUser?.uid || '',
            createdByEmail: currentUser?.email || '',
            createdByName: currentUser?.displayName || currentUser?.name || 'Admin User',
            salonId: salonId,
            scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
            scheduledTime: formData.scheduledTime,
            schedulingType: formData.schedulingType,
            recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
            targetingCriteria: formData.targetingCriteria,
            targetingType: formData.targetingType,
            timestamp: new Date().toISOString(),
            conversionPeriod: Number(formData.conversionPeriod) || 30
          },
          salonId: salonId,
          scheduledFor: scheduledFor,
          sentAt: null,
          status: 'scheduled',
          successCount: 0,
          targetRecipients: targetRecipients,
          title: formData.subject.trim(),
          type: 'whatsapp'
        };

        console.log('Scheduling WhatsApp campaign:', campaignData);
        console.log(`Campaign will target ${targetRecipients.length} customers`);

        // Schedule via backend (saves to Firestore)
        const scheduleResult = await whatsappService.scheduleCampaign(campaignData, salonId);

        // If scheduled time is now (within next 2 minutes), immediately dispatch
        // instead of waiting for the cron scheduler to pick it up
        const twoMinutesFromNow = new Date(Date.now() + 2 * 60 * 1000);
        if (scheduledFor <= twoMinutesFromNow && scheduleResult?.data?.campaignId) {
          console.log('Scheduled time is now — dispatching immediately');
          await whatsappService.sendCampaign(scheduleResult.data.campaignId, salonId);
        }
      }

      // Record deliveries for frequency tracking
      await recordCampaignDeliveries(salonId, 'whatsapp', targetedCustomers);

      let successMessage;
      if (formData.schedulingType === 'one-time') {
        successMessage = `WhatsApp campaign scheduled for ${formData.scheduledDate} at ${formData.scheduledTime} - will target ${targetedCustomers.length} customers.`;
      } else {
        const endInfo = formData.recurring.endType === 'date'
          ? `until ${formData.recurring.endDate}`
          : `for ${formData.recurring.numberOfCycles} cycles`;
        successMessage = `Recurring WhatsApp campaign scheduled (${formData.recurring.frequency}) starting ${formData.recurring.startDate} at ${formData.scheduledTime} ${endInfo} - will target ${targetedCustomers.length} customers.`;
      }

      if (excluded.length > 0) {
        successMessage += ` (${excluded.length} customer(s) excluded by frequency limits)`;
      }
      if (!whatsappConfigured) {
        successMessage += ' Configure WhatsApp Business credentials in the WhatsApp Config tab to enable actual sending via Meta API.';
      }

      setSuccess(successMessage);
      setTimeout(() => setSuccess(''), 5000);

      resetWhatsAppForm();

    } catch (err) {
      console.error('Error scheduling WhatsApp blast:', err);
      setError('Failed to schedule WhatsApp blast: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetWhatsAppForm = () => {
    setSelectedMetaTemplate(null);
    setTemplateVarValues([]);
    setFormData({
      messageCategory: 'not_selected',
      messageType: 'not_selected',
      templateName: '',
      templateLanguage: 'en',
      subject: '',
      body: '',
      imageUrl: '',
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: 'not_selected',
      targetingCriteria: {
        birthMonths: [],
        postalCodes: [],
        lastTransactionMonths: 0,
        lastTransactionUnit: 'months',
        transactionCount: 0,
        transactionType: 'not_selected',
        transactionAmountType: 'not_selected',
        transactionAmount: 0,
        transactionLocationId: 'not_selected',
        memberTiers: [],
        pointsType: 'not_selected',
        pointsAmount: 0,
        specificCustomers: [],
        notesSearchText: '', // #Revised UI_v2 — customer notes targeting
        notesMatchType: 'contains_all', // #Revised UI_v2 — customer notes targeting
        appointmentTimeValue: 0,
        appointmentTimeUnit: 'days',
        appointmentService: 'not_selected',
        appointmentStaff: 'not_selected',
        appointmentStatus: []
      }
    });
    setWhatsappErrors({});
    setFieldErrors({});
    setSelectedCustomers([]);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setTargetingCriteriaSaved(false);
    setImagePreview('');
    setImageError('');
  };


  const handleInputChange = (field, value) => {
    if (field === 'targetingType') {
      setTargetingCriteriaSaved(false);
      setFormData(prev => ({
        ...prev,
        [field]: value,
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          transactionLocationId: 'not_selected',
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all', // #Revised UI_v2 — customer notes targeting
          appointmentTimeValue: 0,
          appointmentTimeUnit: 'days',
          appointmentService: 'not_selected',
          appointmentStaff: 'not_selected',
          appointmentStatus: []
        }
      }));
      setSelectedCustomers([]);

      if (whatsappErrors[field]) {
        setWhatsappErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
      if (fieldErrors[field]) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
      return;
    }

    // Reset message type when category changes
    if (field === 'messageCategory') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        messageType: 'not_selected'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Validate mandatory fields on change
    if (['messageCategory', 'messageType', 'subject', 'body', 'scheduledDate', 'scheduledTime'].includes(field)) {
      validateField(field, value);
    }

    if (whatsappErrors[field]) {
      setWhatsappErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTargetingCriteriaChange = (field, value) => {
    setFormData({
      ...formData,
      targetingCriteria: {
        ...formData.targetingCriteria,
        [field]: value
      }
    });
    setTargetingCriteriaSaved(false);
  };

  const handlePostalCodesChange = (e) => {
    const codes = e.target.value.split(',').map(code => code.trim()).filter(code => code);
    handleTargetingCriteriaChange('postalCodes', codes);
  };

  const handleBirthMonthsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleTargetingCriteriaChange('birthMonths', selectedOptions);
  };

  const handleMemberTiersChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleTargetingCriteriaChange('memberTiers', selectedOptions);
  };

  const validateImageUrl = (url) => {
    if (!url) { setImageError(''); setImagePreview(''); return; }
    try { new URL(url); } catch { setImageError('Please enter a valid URL'); setImagePreview(''); return; }
    const urlLower = url.toLowerCase();
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const hasValidExtension = validExtensions.some(ext => urlLower.includes(ext));
    const commonImageHosts = ['imgur.com', 'giphy.com', 'tenor.com', 'cloudinary.com', 'imagekit.io'];
    const hasImageHost = commonImageHosts.some(host => urlLower.includes(host));
    if (!hasValidExtension && !hasImageHost) {
      setImageError('URL should point to an image (jpg, png, gif, etc.)');
      setImagePreview('');
      return;
    }
    setImageError('');
    setImagePreview(url);
  };

  const handleImageUrlChange = (value) => {
    handleInputChange('imageUrl', value);
    if (value.trim()) validateImageUrl(value);
    else { setImageError(''); setImagePreview(''); }
  };

  const clearImage = () => {
    handleInputChange('imageUrl', '');
    setImagePreview('');
    setImageError('');
  };

  const insertEmoji = (emoji) => {
    const textarea = document.getElementById('whatsappSubject');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = formData.subject.substring(0, start) + emoji + formData.subject.substring(end);
      handleInputChange('subject', newValue);

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    }
  };

  const insertEmojiInBody = (emoji) => {
    const textarea = document.getElementById('whatsappBody');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = formData.body.substring(0, start) + emoji + formData.body.substring(end);
      handleInputChange('body', newValue);

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    }
  };

  const handleRecurringChange = (field, value) => {
    setFormData({
      ...formData,
      recurring: {
        ...formData.recurring,
        [field]: value
      }
    });
  };

  return (
    <div className="notifications-editor">
      <h2>WhatsApp Campaigns</h2>

      {whatsappVerified ? (
        <div className="whatsapp-info-banner whatsapp-connected-banner">
          <div className="info-icon">{'\u2705'}</div>
          <div className="info-content">
            <strong>WhatsApp Messaging Enabled</strong>
            <p>WhatsApp is active for your salon. Messages will be sent via the BespokeBeauty WhatsApp channel with your salon branding.</p>
          </div>
        </div>
      ) : (
        <div className="whatsapp-info-banner">
          <div className="info-icon">{'\u2699\uFE0F'}</div>
          <div className="info-content">
            <strong>WhatsApp Not Enabled</strong>
            <p>WhatsApp messaging has not been enabled for your salon. Please contact your BespokeBeauty administrator to activate WhatsApp for your account. You can still create and draft campaigns here.</p>
          </div>
        </div>
      )}

      {/* Sub-tab Navigation */}
      <div className="sub-tab-navigation">
        <button
          className={`sub-tab-button ${activeSubTab === 'campaign-frequency' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('campaign-frequency')}
        >
          Campaign Frequency
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'create-campaign' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('create-campaign')}
        >
          Create Campaign
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* WhatsApp Campaign Frequency Control */}
      {activeSubTab === 'campaign-frequency' && (
        <CampaignFrequencyControl salonId={salonId} campaignType="whatsapp" />
      )}

      {/* WhatsApp Campaign Templates Section */}
      {activeSubTab === 'create-campaign' && (
        <>
      <div className="templates-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <h3 style={{ margin: 0 }}>WhatsApp Campaign Templates</h3>
          <button
            onClick={() => fetchMetaTemplates(true)}
            disabled={metaTemplatesLoading}
            title="Refresh templates from Meta"
            style={{ fontSize: '12px', padding: '4px 10px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#f8f8f8', color: '#555' }}
          >
            {metaTemplatesLoading ? '…' : '↻ Refresh'}
          </button>
        </div>
        <p className="templates-description">Select a Meta-approved template below. Templates are required by WhatsApp for all campaign messages.</p>

        <div className="template-category-tabs">
          <button
            className={`template-tab ${selectedTemplateCategory === 'transactional' ? 'active' : ''}`}
            onClick={() => setSelectedTemplateCategory('transactional')}
          >
            Transactional
          </button>
          <button
            className={`template-tab ${selectedTemplateCategory === 'promotional' ? 'active' : ''}`}
            onClick={() => setSelectedTemplateCategory('promotional')}
          >
            Promotional
          </button>
        </div>

        {metaTemplatesLoading ? (
          <div className="loading-state" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Loading approved templates from Meta…
          </div>
        ) : metaTemplatesError ? (
          <div className="empty-state" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
            <div className="empty-state-icon">⚠️</div>
            <p style={{ color: '#c0392b', marginBottom: '8px' }}>Failed to load templates: {metaTemplatesError}</p>
            <button
              onClick={() => fetchMetaTemplates(true)}
              style={{ fontSize: '12px', padding: '6px 14px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#f8f8f8' }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="templates-grid">
            {metaTemplates
              .filter(t => {
                const meta = TEMPLATE_META[t.name];
                return meta && meta.category === (selectedTemplateCategory === 'transactional' ? 'transactional' : 'promotional');
              })
              .map((template) => {
                const meta = TEMPLATE_META[template.name];
                if (!meta) return null;
                const isSelected = selectedMetaTemplate?.name === template.name;
                return (
                  <div
                    key={template.name}
                    className={`template-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectMetaTemplate(template)}
                  >
                    <div className="template-card-header">
                      <div className="template-title">{meta.label}</div>
                      <div className="template-badge" style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' }}>Approved</div>
                    </div>
                    <div className="template-preview" style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                      Variables: {meta.vars.join(', ')}
                    </div>
                    {meta.category === 'transactional' && (
                      <div style={{ fontSize: '11px', color: '#2e7d32', marginTop: '6px', padding: '5px 8px', background: '#f1f8f1', borderRadius: '4px', border: '1px solid #cfe6cf' }}>
                        <strong>{campaignSettings[`whatsapp_${template.name}`] !== false ? 'Sent automatically' : 'Automatic sending is OFF'}</strong> {meta.trigger} to customers who opted in to WhatsApp.
                      </div>
                    )}
                    {isSelected && (
                      <div className="template-actions">
                        {meta.category === 'transactional' && (
                          <button
                            className={campaignSettings[`whatsapp_${template.name}`] !== false ? 'template-delete-button' : 'template-load-button'}
                            onClick={(e) => { e.stopPropagation(); handleToggleCampaign({ id: template.name }); }}
                          >
                            {campaignSettings[`whatsapp_${template.name}`] !== false ? 'Disable Campaign' : 'Enable Campaign'}
                          </button>
                        )}
                        <button
                          className="template-load-button"
                          onClick={(e) => { e.stopPropagation(); handleSelectMetaTemplate(template); }}
                        >
                          Use This Template ↓
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            {metaTemplates.filter(t => {
              const meta = TEMPLATE_META[t.name];
              return meta && meta.category === (selectedTemplateCategory === 'transactional' ? 'transactional' : 'promotional');
            }).length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888', width: '100%' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📋</div>
                {metaTemplates.length > 0
                  ? <p>No {selectedTemplateCategory} templates active. Try the other category tab, or check Meta Business Manager.<br/><small style={{color:'#aaa'}}>{metaTemplates.length} template(s) loaded from Meta.</small></p>
                  : <p>No approved templates yet — templates are pending Meta review.</p>
                }
                <button
                  onClick={() => fetchMetaTemplates(true)}
                  style={{ fontSize: '12px', padding: '6px 14px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#f8f8f8', marginTop: '8px' }}
                >
                  Refresh from Meta
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {previewLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">Loading customer preview...</div>
          </div>
        </div>
      )}

      <CustomerPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        previewData={previewData}
        campaignName={formData.subject || 'whatsapp campaign'}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <div className="modal-header">
              <h3>Delete Template</h3>
              <button className="modal-close-btn" onClick={() => setShowDeleteConfirm(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this template?</p>
              <p className="delete-warning">
                <strong>{selectedTemplate?.title}</strong>
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-button"
                onClick={confirmDeleteTemplate}
              >
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="notification-form">
        <h3>Create WhatsApp Campaign</h3>

        <div className="form-section">
          <h4>Message Category</h4>

          <div className="form-group">
            <label htmlFor="messageCategory">
              Category<span className="required-asterisk">*</span>
            </label>
            <select
              id="messageCategory"
              name="messageCategory"
              value={formData.messageCategory}
              onChange={(e) => handleInputChange('messageCategory', e.target.value)}
              onBlur={handleFieldBlur}
              className={fieldErrors.messageCategory ? 'input-error' : ''}
              required
            >
              <option value="not_selected">Select Category</option>
              <option value="transactional">Transactional Messages</option>
              <option value="promotional">Promotional Messages</option>
            </select>
            {fieldErrors.messageCategory && <span className="field-error-message">{fieldErrors.messageCategory}</span>}
            <div className="form-helper-text">
              Transactional: Appointment updates, payment confirmations, loyalty points. Promotional: Special offers and promotions.
            </div>
          </div>

          {formData.messageCategory === 'transactional' && (
            <div className="form-group">
              <label htmlFor="messageType">
                Message Type<span className="required-asterisk">*</span>
              </label>
              <select
                id="messageType"
                name="messageType"
                value={formData.messageType}
                onChange={(e) => handleInputChange('messageType', e.target.value)}
                onBlur={handleFieldBlur}
                className={fieldErrors.messageType ? 'input-error' : ''}
                required
              >
                <option value="not_selected">Select Message Type</option>
                <option value="appointment_update">Appointment Update</option>
                <option value="payment_update">Payment Update</option>
                <option value="loyalty_update">Loyalty Points Update</option>
              </select>
              {fieldErrors.messageType && <span className="field-error-message">{fieldErrors.messageType}</span>}
            </div>
          )}
        </div>

        <div className="form-section">
          <h4>Message Content</h4>

          <div className="form-group">
            <label htmlFor="whatsappSubject">
              Message Title<span className="required-asterisk">*</span>
            </label>
            <div className="input-with-emojis">
              <input
                type="text"
                id="whatsappSubject"
                name="subject"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                onBlur={handleFieldBlur}
                className={fieldErrors.subject ? 'input-error' : ''}
                maxLength={100}
                required
              />
              {fieldErrors.subject && <span className="field-error-message">{fieldErrors.subject}</span>}
              <div className="emoji-picker">
                <span>Add emoji:</span>
                {commonEmojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    className="emoji-button"
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-helper-text">
              Max 100 characters - This will appear as the message preview
            </div>
          </div>

          {selectedMetaTemplate && TEMPLATE_META[selectedMetaTemplate.name] ? (
            <div className="form-group">
              <label>Template Variables</label>
              {whatsappErrors.templateVars && (
                <span className="field-error-message" style={{ display: 'block', marginBottom: '8px' }}>{whatsappErrors.templateVars}</span>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {templateVarValues.map((v, idx) => (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: v.isAuto ? '#666' : '#333' }}>
                      {`{{${idx + 1}}} ${v.varName}`}
                      {v.isAuto && (
                        <span style={{ marginLeft: '6px', fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                          — auto-filled at send time
                        </span>
                      )}
                      {!v.isAuto && <span style={{ color: 'red' }}> *</span>}
                    </label>
                    <input
                      type="text"
                      value={v.value}
                      disabled={v.isAuto}
                      placeholder={v.isAuto ? `(resolved from customer data)` : `Enter ${v.varName.toLowerCase()}…`}
                      style={{ background: v.isAuto ? '#f5f5f5' : '#fff', color: v.isAuto ? '#999' : '#333', cursor: v.isAuto ? 'not-allowed' : 'text' }}
                      onChange={(e) => {
                        const updated = [...templateVarValues];
                        updated[idx] = { ...updated[idx], value: e.target.value };
                        setTemplateVarValues(updated);
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="form-helper-text" style={{ marginTop: '8px' }}>
                Auto-filled variables are resolved per customer at send time. Fill in all required (*) fields.
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="whatsappBody">
                Message Body<span className="required-asterisk">*</span>
              </label>
              <div className="textarea-with-emojis">
                <textarea
                  id="whatsappBody"
                  name="body"
                  value={formData.body}
                  onChange={(e) => handleInputChange('body', e.target.value)}
                  onBlur={handleFieldBlur}
                  rows="8"
                  placeholder="Write your WhatsApp message here...

Keep it clear and engaging.
Max 1000 characters."
                  className={fieldErrors.body ? 'input-error' : ''}
                  maxLength={1000}
                  required
                />
                {fieldErrors.body && <span className="field-error-message">{fieldErrors.body}</span>}
                <div className="emoji-picker">
                  <span>Add emoji:</span>
                  {commonEmojis.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      className="emoji-button"
                      onClick={() => insertEmojiInBody(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-helper-text">
                Max 1000 characters - WhatsApp messages support formatting with *bold*, _italic_, and ~strikethrough~
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="whatsappImageUrl">
              Image / GIF URL (Optional)
              {imageError && <span className="error-text"> — {imageError}</span>}
            </label>
            <div className="image-url-input-container">
              <input
                type="url"
                id="whatsappImageUrl"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                placeholder="https://example.com/image.jpg or https://media.giphy.com/..."
                className={imageError ? 'error' : ''}
              />
              {formData.imageUrl && (
                <button
                  type="button"
                  className="clear-image-button"
                  onClick={clearImage}
                  title="Clear image"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="form-helper-text">
              The image will be sent first with your message as the caption. JPG and PNG display as photos; GIFs are delivered as animated documents.
            </div>
          </div>
        </div>

        {(formData.subject || formData.body) && (
          <div className="form-section">
            <h4>Message Preview</h4>
            {/* Device selector pills */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
              <button type="button" onClick={() => setPreviewDevice('mobile')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '20px', border: `1.5px solid ${previewDevice === 'mobile' ? '#075e54' : '#ddd'}`, background: previewDevice === 'mobile' ? '#e8f5e9' : '#fafafa', color: previewDevice === 'mobile' ? '#075e54' : '#888', fontWeight: previewDevice === 'mobile' ? '600' : '400', fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s' }}>
                <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="9" height="13" rx="1.5" stroke="currentColor" strokeWidth="1"/><rect x="3.5" y="11" width="3" height="1" rx="0.5" fill="currentColor"/></svg>
                Mobile
              </button>
              <button type="button" onClick={() => setPreviewDevice('laptop')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '20px', border: `1.5px solid ${previewDevice === 'laptop' ? '#075e54' : '#ddd'}`, background: previewDevice === 'laptop' ? '#e8f5e9' : '#fafafa', color: previewDevice === 'laptop' ? '#075e54' : '#888', fontWeight: previewDevice === 'laptop' ? '600' : '400', fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s' }}>
                <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="13" height="8" rx="1" stroke="currentColor" strokeWidth="1"/><rect x="2" y="2" width="10" height="5.5" rx="0.3" fill="currentColor" opacity="0.18"/><rect x="0" y="9" width="14" height="1.5" rx="0.75" fill="currentColor" opacity="0.5"/></svg>
                Laptop
              </button>
            </div>
            {/* Device frame */}
            <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
              {previewDevice === 'mobile' ? (
                /* Realistic iPhone frame — 86px wide, 495px screen height */
                <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  {/* Mute switch */}
                  <div style={{ position: 'absolute', left: '-3px', top: '58px', width: '3px', height: '11px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                  {/* Volume up */}
                  <div style={{ position: 'absolute', left: '-3px', top: '76px', width: '3px', height: '20px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                  {/* Volume down */}
                  <div style={{ position: 'absolute', left: '-3px', top: '102px', width: '3px', height: '20px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                  {/* Power button */}
                  <div style={{ position: 'absolute', right: '-3px', top: '82px', width: '3px', height: '28px', background: '#3a3a3a', borderRadius: '0 2px 2px 0', boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.1)' }} />
                  {/* iPhone body */}
                  <div style={{ width: '224px', background: 'linear-gradient(170deg,#2d2d2d 0%,#1a1a1a 50%,#232323 100%)', borderRadius: '22px', overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 22px 55px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.1)' }}>
                    {/* Top bezel + Dynamic Island */}
                    <div style={{ background: '#000', paddingTop: '10px', paddingBottom: '5px', display: 'flex', justifyContent: 'center' }}>
                      <div style={{ width: '50px', height: '14px', background: '#0d0d0d', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '5px', boxShadow: '0 0 0 0.5px #222' }}>
                        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#151515', boxShadow: '0 0 0 0.5px #2a2a2a, inset 0 0 2px rgba(0,100,200,0.25)' }} />
                      </div>
                    </div>
                    {/* Screen — 495px, vertical scroll only */}
                    <div style={{ height: '362px', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin', background: '#e5ddd5', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                      {/* Sticky chat header */}
                      <div style={{ background: '#075e54', padding: '8px 8px', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, position: 'sticky', top: 0, zIndex: 1 }}>
                        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>‹</div>
                        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#128c7e', border: '1.5px solid rgba(255,255,255,0.3)', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '8.5px', fontWeight: '600', color: '#fff', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{salonName || 'Bespoke Beauty'}</div>
                          <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.7)' }}>online</div>
                        </div>
                      </div>
                      {/* Chat body */}
                      <div style={{ padding: '8px 7px' }}>
                        <div style={{ maxWidth: '88%', marginLeft: 'auto' }}>
                          <div style={{ background: '#dcf8c6', borderRadius: '10px 2px 10px 10px', padding: '6px 8px', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' }}>
                            {imagePreview && !imageError && (
                              <img src={imagePreview} alt="" onError={() => { setImageError('Failed to load image. Please check the URL.'); setImagePreview(''); }} style={{ width: '100%', borderRadius: '6px', objectFit: 'cover', marginBottom: '5px', display: 'block' }} />
                            )}
                            {formData.subject && (
                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#111', marginBottom: '3px', wordBreak: 'break-word' }}>{formData.subject}</div>
                            )}
                            <div style={{ fontSize: '8.5px', color: '#333', lineHeight: '1.4', wordBreak: 'break-word' }}>
                              {formData.body || <span style={{ fontStyle: 'italic', color: '#aaa' }}>Message body…</span>}
                            </div>
                            <div style={{ textAlign: 'right', fontSize: '7.5px', color: '#667781', marginTop: '4px' }}>now ✓✓</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Bottom bezel + home indicator */}
                    <div style={{ background: '#000', paddingTop: '6px', paddingBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                      <div style={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.22)', borderRadius: '3px' }} />
                    </div>
                  </div>
                </div>
              ) : (
                /* Laptop frame — 2× width (560px) and 2× screen height (316px), scrollable */
                <div style={{ display: 'inline-block', flexShrink: 0 }}>
                  <div style={{ width: '483px', background: '#1c1c1e', borderRadius: '8px 8px 0 0', padding: '8px 7px 0', boxSizing: 'border-box', boxShadow: 'inset 0 0 0 1px #3a3a3a' }}>
                    <div style={{ width: '4px', height: '4px', background: '#444', borderRadius: '50%', margin: '0 auto 4px' }} />
                    <div style={{ background: '#f0f2f5', borderRadius: '3px 3px 0 0', height: '276px', overflowY: 'hidden', display: 'flex', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                      {/* Sidebar */}
                      <div style={{ width: '100px', background: '#fff', borderRight: '1px solid #e9edef', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ background: '#075e54', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#128c7e', border: '1px solid rgba(255,255,255,0.3)', flexShrink: 0 }} />
                          <div style={{ fontSize: '8px', fontWeight: '600', color: '#fff', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{salonName || 'Bespoke'}</div>
                        </div>
                        <div style={{ padding: '6px 8px', background: '#f0f2f5', borderBottom: '1px solid #e9edef' }}>
                          <div style={{ fontSize: '8px', fontWeight: '600', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{salonName || 'Bespoke Beauty'}</div>
                          <div style={{ fontSize: '7px', color: '#667781', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '1px' }}>{formData.body ? formData.body.substring(0, 18) + (formData.body.length > 18 ? '…' : '') : 'Message…'}</div>
                        </div>
                      </div>
                      {/* Chat pane */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ background: '#f0f2f5', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid #e9edef', flexShrink: 0 }}>
                          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#128c7e', flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: '8.5px', fontWeight: '600', color: '#111' }}>{salonName || 'Bespoke Beauty'}</div>
                            <div style={{ fontSize: '7px', color: '#667781' }}>online</div>
                          </div>
                        </div>
                        <div style={{ flex: 1, background: '#e5ddd5', padding: '8px', overflowY: 'auto', scrollbarWidth: 'thin' }}>
                          <div style={{ alignSelf: 'flex-end', maxWidth: '88%', marginLeft: 'auto' }}>
                            <div style={{ background: '#dcf8c6', borderRadius: '7px 0 7px 7px', padding: '5px 7px', boxShadow: '0 1px 1px rgba(0,0,0,0.1)' }}>
                              {imagePreview && !imageError && (
                                <img src={imagePreview} alt="" onError={() => { setImageError('Failed to load image. Please check the URL.'); setImagePreview(''); }} style={{ width: '100%', borderRadius: '4px', objectFit: 'cover', marginBottom: '4px', display: 'block' }} />
                              )}
                              {formData.subject && (
                                <div style={{ fontSize: '9px', fontWeight: '700', color: '#111', marginBottom: '3px', wordBreak: 'break-word' }}>{formData.subject}</div>
                              )}
                              <div style={{ fontSize: '8.5px', color: '#333', lineHeight: '1.4', wordBreak: 'break-word' }}>
                                {formData.body || <span style={{ fontStyle: 'italic', color: '#aaa' }}>Message body…</span>}
                              </div>
                              <div style={{ textAlign: 'right', fontSize: '7px', color: '#999', marginTop: '3px' }}>now ✓✓</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ width: '483px', height: '10px', background: '#2c2c2e' }} />
                  <div style={{ width: '515px', height: '8px', background: '#1c1c1e', borderRadius: '0 0 6px 6px', marginLeft: '-16px', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }} />
                </div>
              )}
            </div>
            <div className="form-helper-text" style={{ marginTop: '10px', textAlign: 'center' }}>
              Preview of how the message will appear in a recipient's WhatsApp {previewDevice === 'mobile' ? 'app' : 'Web'}.
            </div>
          </div>
        )}

        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Scheduling</h4>

          <div className="form-group">
            <label>Campaign Type *</label>
            <div className="scheduling-type-tabs">
              <button
                type="button"
                className={`scheduling-tab ${formData.schedulingType === 'one-time' ? 'active' : ''}`}
                onClick={() => handleInputChange('schedulingType', 'one-time')}
                disabled={templateEditMode}
              >
                One-Time Campaign
              </button>
              <button
                type="button"
                className={`scheduling-tab ${formData.schedulingType === 'recurring' ? 'active' : ''}`}
                onClick={() => handleInputChange('schedulingType', 'recurring')}
                disabled={templateEditMode}
              >
                Recurring Campaign
              </button>
            </div>
          </div>

          {formData.schedulingType === 'one-time' ? (
            <div className="one-time-scheduling">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="scheduledDate">
                    Scheduled Date<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    id="scheduledDate"
                    name="scheduledDate"
                    value={formData.scheduledDate}
                    onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                    onBlur={handleFieldBlur}
                    min={new Date().toISOString().split('T')[0]}
                    className={fieldErrors.scheduledDate ? 'input-error' : ''}
                    disabled={templateEditMode}
                    required
                  />
                  {fieldErrors.scheduledDate && <span className="field-error-message">{fieldErrors.scheduledDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="scheduledTime">
                    Scheduled Time<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="time"
                    id="scheduledTime"
                    name="scheduledTime"
                    value={formData.scheduledTime}
                    onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                    onBlur={handleFieldBlur}
                    className={fieldErrors.scheduledTime ? 'input-error' : ''}
                    disabled={templateEditMode}
                    required
                  />
                  {fieldErrors.scheduledTime && <span className="field-error-message">{fieldErrors.scheduledTime}</span>}
                </div>
              </div>

              {whatsappErrors.scheduledDateTime && (
                <div className="error-message">
                  {whatsappErrors.scheduledDateTime}
                </div>
              )}
            </div>
          ) : (
            <div className="recurring-scheduling">
              <div className="form-group">
                <label htmlFor="recurringFrequency">
                  Frequency *
                </label>
                <select
                  id="recurringFrequency"
                  value={formData.recurring.frequency}
                  onChange={(e) => handleRecurringChange('frequency', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>

              {formData.recurring.frequency === 'weekly' && (
                <div className="form-group">
                  <label htmlFor="dayOfWeek">Day of Week *</label>
                  <select
                    id="dayOfWeek"
                    value={formData.recurring.dayOfWeek}
                    onChange={(e) => handleRecurringChange('dayOfWeek', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
              )}

              {formData.recurring.frequency === 'monthly' && (
                <div className="form-group">
                  <label htmlFor="dayOfMonth">Day of Month *</label>
                  <input
                    type="number"
                    id="dayOfMonth"
                    min="1"
                    max="31"
                    value={formData.recurring.dayOfMonth}
                    onChange={(e) => handleRecurringChange('dayOfMonth', parseInt(e.target.value))}
                    disabled={templateEditMode}
                  />
                  <div className="form-helper-text">Enter a number between 1-31</div>
                </div>
              )}

              {formData.recurring.frequency === 'annually' && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="monthOfYear">Month *</label>
                    <select
                      id="monthOfYear"
                      value={formData.recurring.monthOfYear}
                      onChange={(e) => handleRecurringChange('monthOfYear', e.target.value)}
                      disabled={templateEditMode}
                    >
                      <option value="january">January</option>
                      <option value="february">February</option>
                      <option value="march">March</option>
                      <option value="april">April</option>
                      <option value="may">May</option>
                      <option value="june">June</option>
                      <option value="july">July</option>
                      <option value="august">August</option>
                      <option value="september">September</option>
                      <option value="october">October</option>
                      <option value="november">November</option>
                      <option value="december">December</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="dayOfYear">Day *</label>
                    <input
                      type="number"
                      id="dayOfYear"
                      min="1"
                      max="31"
                      value={formData.recurring.dayOfYear}
                      onChange={(e) => handleRecurringChange('dayOfYear', parseInt(e.target.value))}
                      disabled={templateEditMode}
                    />
                    <div className="form-helper-text">Day of the month (1-31)</div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="recurringTime">Time *</label>
                <input
                  type="time"
                  id="recurringTime"
                  value={formData.scheduledTime}
                  onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">Time when the campaign should run</div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recurringStartDate">Start Date *</label>
                  <input
                    type="date"
                    id="recurringStartDate"
                    value={formData.recurring.startDate}
                    onChange={(e) => handleRecurringChange('startDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={templateEditMode}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Campaign End *</label>
                <div className="recurring-end-type">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="endType"
                      value="date"
                      checked={formData.recurring.endType === 'date'}
                      onChange={(e) => handleRecurringChange('endType', e.target.value)}
                      disabled={templateEditMode}
                    />
                    <span>End on specific date</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="endType"
                      value="cycles"
                      checked={formData.recurring.endType === 'cycles'}
                      onChange={(e) => handleRecurringChange('endType', e.target.value)}
                      disabled={templateEditMode}
                    />
                    <span>End after number of cycles</span>
                  </label>
                </div>
              </div>

              {formData.recurring.endType === 'date' ? (
                <div className="form-group">
                  <label htmlFor="recurringEndDate">End Date *</label>
                  <input
                    type="date"
                    id="recurringEndDate"
                    value={formData.recurring.endDate}
                    onChange={(e) => handleRecurringChange('endDate', e.target.value)}
                    min={formData.recurring.startDate || new Date().toISOString().split('T')[0]}
                    disabled={templateEditMode}
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="numberOfCycles">Number of Cycles *</label>
                  <input
                    type="number"
                    id="numberOfCycles"
                    min="1"
                    value={formData.recurring.numberOfCycles}
                    onChange={(e) => handleRecurringChange('numberOfCycles', parseInt(e.target.value))}
                    disabled={templateEditMode}
                  />
                  <div className="form-helper-text">How many times should this campaign run?</div>
                </div>
              )}

              <div className="form-group" style={{ marginTop: '25px', paddingTop: '25px', borderTop: '2px solid #e0e0e0' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.recurring.excludePreviousRecipients}
                    onChange={(e) => handleRecurringChange('excludePreviousRecipients', e.target.checked)}
                    disabled={templateEditMode}
                  />
                  <span>Exclude previous recipients from each campaign run</span>
                </label>
                <div className="form-helper-text">
                  Prevent customers who received this campaign in the past from receiving it again
                </div>
              </div>

              {formData.recurring.excludePreviousRecipients && (
                <div className="exclusion-settings">
                  <div className="form-group">
                    <label>Exclusion Period</label>
                    <div className="recurring-end-type">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="exclusionType"
                          value="cycles"
                          checked={formData.recurring.exclusionType === 'cycles'}
                          onChange={(e) => handleRecurringChange('exclusionType', e.target.value)}
                          disabled={templateEditMode}
                        />
                        <span>Exclude from past X cycles</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="exclusionType"
                          value="date"
                          checked={formData.recurring.exclusionType === 'date'}
                          onChange={(e) => handleRecurringChange('exclusionType', e.target.value)}
                          disabled={templateEditMode}
                        />
                        <span>Exclude who received before specific date</span>
                      </label>
                    </div>
                  </div>

                  {formData.recurring.exclusionType === 'cycles' ? (
                    <div className="form-group">
                      <label htmlFor="excludePastCycles">Exclude past cycles</label>
                      <input
                        type="number"
                        id="excludePastCycles"
                        min="1"
                        value={formData.recurring.excludePastCycles}
                        onChange={(e) => handleRecurringChange('excludePastCycles', parseInt(e.target.value))}
                        disabled={templateEditMode}
                      />
                      <div className="form-helper-text">
                        Exclude customers who received this in the last X campaign runs
                      </div>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="excludeBeforeDate">Exclude before date</label>
                      <input
                        type="date"
                        id="excludeBeforeDate"
                        value={formData.recurring.excludeBeforeDate}
                        onChange={(e) => handleRecurringChange('excludeBeforeDate', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        disabled={templateEditMode}
                      />
                      <div className="form-helper-text">
                        Exclude customers who received campaigns of this type before this date
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Conversion Tracking</h4>
          <div className="form-group">
            <label htmlFor="conversionPeriod">
              Conversion Period (days)
            </label>
            <input
              type="number"
              id="conversionPeriod"
              name="conversionPeriod"
              min="1"
              max="365"
              value={formData.conversionPeriod}
              onChange={(e) => handleInputChange('conversionPeriod', Math.max(1, parseInt(e.target.value, 10) || 30))}
              disabled={templateEditMode}
              style={{ width: 120 }}
            />
            <div className="form-helper-text">
              Bookings made within this many days of the campaign being sent will be attributed as conversions from this campaign.
            </div>
          </div>
        </div>

        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Customer Targeting</h4>

          <div className="form-group">
            <label htmlFor="targetingType">Target Audience<span className="required-asterisk">*</span></label>
            <select
              id="targetingType"
              name="targetingType"
              value={formData.targetingType}
              onChange={(e) => handleInputChange('targetingType', e.target.value)}
              onBlur={handleFieldBlur}
              className={fieldErrors.targetingType ? 'input-error' : ''}
              disabled={templateEditMode}
            >
              <option value="not_selected">Not Selected</option>
              <option value="all_customers">All Customers</option>
              <option value="customer_profile">Customer Profile Based</option>
              <option value="customer_transaction">Customer Transaction Based</option>
              <option value="customer_points">Customer Points Based</option>
              <option value="customer_appointment">Customer Appointment Based</option>
              <option value="specific_customer">Specific Customer</option>
              <option value="customer_notes">Customer Notes Based</option>
            </select>
            {fieldErrors.targetingType && <span className="field-error-message">{fieldErrors.targetingType}</span>}
          </div>

          {formData.targetingType === 'customer_profile' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Birth Months</label>
                <select
                  multiple
                  value={formData.targetingCriteria.birthMonths}
                  onChange={handleBirthMonthsChange}
                  size="6"
                  disabled={templateEditMode}
                >
                  {birthMonthOptions.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple months
                </div>
              </div>

              <div className="form-group">
                <label>Postal Codes (comma-separated)</label>
                <input
                  type="text"
                  value={formData.targetingCriteria.postalCodes.join(', ')}
                  onChange={handlePostalCodesChange}
                  placeholder="e.g., 560, 123456, 567890"
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">
                  Leave empty for all postal codes. Use partial codes for broader targeting.
                </div>
              </div>
            </div>
          )}

          {formData.targetingType === 'customer_transaction' && (
            <div className="targeting-criteria">
              {isMultiLocation && (
                <div className="form-group">
                  <label>Location Name</label>
                  <select
                    value={formData.targetingCriteria.transactionLocationId || 'not_selected'}
                    onChange={(e) => handleTargetingCriteriaChange('transactionLocationId', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="not_selected">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.locationId} value={loc.locationId}>
                        {loc.locationName}{loc.isHeadOffice ? ' (Head Office)' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="form-helper-text">
                    Filter by transactions at a specific location, or leave as "All Locations".
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Transaction Count Criteria</label>
                <select
                  value={formData.targetingCriteria.transactionType}
                  onChange={(e) => handleTargetingCriteriaChange('transactionType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="less_than">Less than X transactions</option>
                  <option value="equal_to">Exactly X transactions</option>
                  <option value="greater_than">More than X transactions</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Number of Transactions</label>
                  <input
                    type="number"
                    value={formData.targetingCriteria.transactionCount}
                    onChange={(e) => handleTargetingCriteriaChange('transactionCount', Number(e.target.value))}
                    min="0"
                    disabled={templateEditMode}
                  />
                </div>
                <div className="form-group">
                  <label>Last Transaction within</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={formData.targetingCriteria.lastTransactionMonths}
                      onChange={(e) => handleTargetingCriteriaChange('lastTransactionMonths', Number(e.target.value))}
                      min="0"
                      disabled={templateEditMode}
                      style={{ width: '80px' }}
                    />
                    <select
                      value={formData.targetingCriteria.lastTransactionUnit || 'months'}
                      onChange={(e) => handleTargetingCriteriaChange('lastTransactionUnit', e.target.value)}
                      disabled={templateEditMode}
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Spend Amount Criteria</label>
                <select
                  value={formData.targetingCriteria.transactionAmountType}
                  onChange={(e) => handleTargetingCriteriaChange('transactionAmountType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="greater_than">Greater Than the Following Amount</option>
                  <option value="less_than">Less Than the Following Amount</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount ($)</label>
                <input
                  type="number"
                  value={formData.targetingCriteria.transactionAmount}
                  onChange={(e) => handleTargetingCriteriaChange('transactionAmount', Number(e.target.value))}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  disabled={templateEditMode}
                />
              </div>
            </div>
          )}

          {formData.targetingType === 'customer_points' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Member Tiers</label>
                <select
                  multiple
                  value={formData.targetingCriteria.memberTiers}
                  onChange={handleMemberTiersChange}
                  size="5"
                  disabled={templateEditMode}
                >
                  {memberTierOptions.map(tier => (
                    <option key={tier} value={tier}>{tier}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple tiers
                </div>
              </div>

              <div className="form-group">
                <label>Points Criteria</label>
                <select
                  value={formData.targetingCriteria.pointsType}
                  onChange={(e) => handleTargetingCriteriaChange('pointsType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="greater_than">Greater Than the Following Number of Points</option>
                  <option value="less_than">Less Than the Following Number of Points</option>
                </select>
              </div>

              <div className="form-group">
                <label>Number of Points</label>
                <input
                  type="number"
                  value={formData.targetingCriteria.pointsAmount}
                  onChange={(e) => handleTargetingCriteriaChange('pointsAmount', Number(e.target.value))}
                  min="0"
                  placeholder="0"
                  disabled={templateEditMode}
                />
              </div>
            </div>
          )}

          {formData.targetingType === 'customer_appointment' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Upcoming Request within</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={formData.targetingCriteria.appointmentTimeValue}
                    onChange={(e) => handleTargetingCriteriaChange('appointmentTimeValue', Number(e.target.value))}
                    min="0"
                    disabled={templateEditMode}
                    style={{ width: '80px' }}
                  />
                  <select
                    value={formData.targetingCriteria.appointmentTimeUnit}
                    onChange={(e) => handleTargetingCriteriaChange('appointmentTimeUnit', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Service Requested</label>
                <select
                  value={formData.targetingCriteria.appointmentService}
                  onChange={(e) => handleTargetingCriteriaChange('appointmentService', e.target.value)}
                  disabled={templateEditMode || loadingServicesStaff}
                >
                  <option value="not_selected">Not Selected (Any Service)</option>
                  {salonServices.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
                {loadingServicesStaff && <span className="form-helper-text">Loading services...</span>}
              </div>

              <div className="form-group">
                <label>Staff Requested</label>
                <select
                  value={formData.targetingCriteria.appointmentStaff}
                  onChange={(e) => handleTargetingCriteriaChange('appointmentStaff', e.target.value)}
                  disabled={templateEditMode || loadingServicesStaff}
                >
                  <option value="not_selected">Not Selected (Any Staff)</option>
                  {salonStaff.map(staff => (
                    <option key={staff.id} value={staff.id}>{staff.name}</option>
                  ))}
                </select>
                {loadingServicesStaff && <span className="form-helper-text">Loading staff...</span>}
              </div>

              <div className="form-group">
                <label>Appointment Status</label>
                <select
                  multiple
                  value={formData.targetingCriteria.appointmentStatus}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                    handleTargetingCriteriaChange('appointmentStatus', selectedOptions);
                  }}
                  size="4"
                  disabled={templateEditMode}
                >
                  {appointmentStatusOptions.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple statuses. Leave empty for any status.
                </div>
              </div>
            </div>
          )}

          {formData.targetingType === 'specific_customer' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>
                  Search and Select Customers
                  {whatsappErrors.specificCustomers && <span className="error-text"> - {whatsappErrors.specificCustomers}</span>}
                </label>
                <div className="customer-search-container">
                  <input
                    type="text"
                    value={customerSearchQuery}
                    onChange={handleCustomerSearchChange}
                    onFocus={() => setShowCustomerDropdown(true)}
                    placeholder="Search by customer name, phone or email..."
                    className="customer-search-input"
                    disabled={templateEditMode}
                  />

                  {showCustomerDropdown && (
                    <div className="customer-search-dropdown">
                      {searchingCustomers && (
                        <div className="customer-search-loading">Searching...</div>
                      )}

                      {!searchingCustomers && customerSearchResults.length === 0 && customerSearchQuery.length >= 2 && (
                        <div className="customer-search-no-results">No customers found</div>
                      )}

                      {!searchingCustomers && customerSearchResults.length > 0 && (
                        <>
                          {customerSearchResults.map(customer => (
                            <div
                              key={customer.id}
                              className="customer-search-result"
                              onClick={() => handleCustomerSelect(customer)}
                            >
                              <div className="customer-result-name">{customer.displayName}</div>
                              <div className="customer-result-email">{customer.email}</div>
                              {customer.phoneNumber && (
                                <div className="customer-result-phone">{customer.phoneNumber}</div>
                              )}
                            </div>
                          ))}
                        </>
                      )}

                      {customerSearchQuery.length < 2 && (
                        <div className="customer-search-hint">Type at least 2 characters to search</div>
                      )}
                    </div>
                  )}
                </div>

                {selectedCustomers.length > 0 && (
                  <div className="selected-customers">
                    <h5>Selected Customers ({selectedCustomers.length})</h5>
                    <div className="selected-customers-list">
                      {selectedCustomers.map(customer => (
                        <div key={customer.id} className="selected-customer-item">
                          <div className="selected-customer-info">
                            <div className="selected-customer-name">{customer.displayName}</div>
                            <div className="selected-customer-email">{customer.email}</div>
                            {customer.phoneNumber && (
                              <div className="selected-customer-phone">{customer.phoneNumber}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            className="remove-customer-button"
                            onClick={() => handleCustomerRemove(customer.id)}
                            disabled={templateEditMode}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* #Revised UI_v2 — customer notes targeting */}
          {formData.targetingType === 'customer_notes' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Search Terms</label>
                <input
                  type="text"
                  value={formData.targetingCriteria.notesSearchText || ''}
                  onChange={(e) => handleTargetingCriteriaChange('notesSearchText', e.target.value)}
                  placeholder="Enter words or phrase to search in customer notes..."
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">Customers whose notes match the search terms will be targeted</div>
              </div>
              <div className="form-group">
                <label>Match Type</label>
                <select
                  value={formData.targetingCriteria.notesMatchType || 'contains_all'}
                  onChange={(e) => handleTargetingCriteriaChange('notesMatchType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="exact">Exact Match — notes contain this exact phrase</option>
                  <option value="contains_any">Contains Any Words — notes contain at least one of the words</option>
                  <option value="contains_all">Contains All Words — notes contain all words (any order)</option>
                </select>
              </div>
            </div>
          )}

          <div className="targeting-buttons">
            <button
              type="button"
              className="save-criteria-button"
              onClick={handleSaveTargetingCriteria}
              disabled={formData.targetingType === 'not_selected' || templateEditMode}
            >
              Save Targeting Criteria
            </button>
            <button
              type="button"
              className="preview-button"
              onClick={handlePreviewTargeting}
              disabled={previewLoading || !targetingCriteriaSaved || templateEditMode}
              style={{ opacity: targetingCriteriaSaved ? 1 : 0.5 }}
            >
              {previewLoading ? 'Loading...' : 'Preview Target Customers'}
            </button>
            {!templateEditMode && (
              <button
                type="button"
                className="add-template-button"
                onClick={handleCreateTemplate}
                disabled={formData.targetingType === 'not_selected'}
              >
                Add as Template
              </button>
            )}
            {!templateEditMode && (
              <button
                type="button"
                className="download-social-csv-button"
                onClick={handleDownloadSocialCSV}
                disabled={!targetingCriteriaSaved}
                style={{ opacity: targetingCriteriaSaved ? 1 : 0.5 }}
              >
                Download Target for Social Platforms (Meta, Instagram, TikTok)
              </button>
            )}
          </div>
        </div>

        {/* Final Customers Targeted Section */}
        <FinalCustomersTargeted
          salonId={salonId}
          campaignType="whatsapp"
          formData={formData}
          targetingCriteriaSaved={targetingCriteriaSaved}
          disabled={templateEditMode}
        />


        {templateEditMode ? (
          <div className="template-edit-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleSaveRevisedTemplate}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Make Revisions to Template'}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setTemplateEditMode(false);
                setTemplateToEdit(null);
                resetWhatsAppForm();
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="campaign-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={resetWhatsAppForm}
          >
            Cancel
          </button>

          <button
            type="button"
            className="test-blast-button"
            onClick={handleSendTestWhatsAppBlast}
            disabled={saving}
          >
            {saving ? 'Sending...' : 'Send Test Blast'}
          </button>

          <button
            type="button"
            className="actual-blast-button"
            onClick={handleSendActualWhatsAppBlast}
            disabled={saving}
          >
            {saving ? 'Scheduling...' : 'Send Actual Blast'}
          </button>
        </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

export default WhatsAppCampaignsEditor;
